/**
 * Elite Batch 3 Part 4 — Workflow Templates 227–247
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsAppend(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 3 PART 4 — TEMPLATES 227–247
// =============================================================================

export const ELITE_BATCH3_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 227. MIXPOST QUEUE BUILDER (7 nodes)
  // ===================================================================

  "Mixpost Queue Builder": {
    summary:
      "Runs weekly to pull the content calendar from Google Sheets, generates an AI weekly posting plan, drafts individual posts, schedules them into Mixpost via API, sends the batch to Slack for team review, and logs performance metrics for tracking.",
    nodes: [
      trigger("Weekly: Build Mixpost queue", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Fires every Monday morning to build and schedule the upcoming week's social media queue in Mixpost",
        setup:
          "Set the schedule to your preferred weekly cadence. Ensure your Google Sheets content calendar has columns for topic, platform, target_date, and notes. Configure your Mixpost API key and Slack webhook.",
        inputFields: [],
      }),

      // 2. Pull content calendar from Google Sheets
      n("2", "hitApi", "Pull content calendar from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/ContentCalendar!A2:F20",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "values", customName: "calendar_rows", type: "string" },
            { name: "range", customName: "calendar_range", type: "string" },
          ],
        },
      }),

      // 3. AI weekly posting plan
      aiNode(
        "3",
        "AI generate weekly posting plan",
        520,
        "You are a social media strategist. Given a content calendar with topics, platforms, and dates, create a detailed weekly posting plan. For each entry, decide the best time, format (carousel, thread, single post), and tone. Respond JSON: {weekly_plan: string, post_count: number, platforms_covered: string, content_themes: string, schedule_summary: string}",
        "Content calendar data: {calendar_rows}",
        [
          { field: "weekly_plan", as: "weekly_plan", type: "string" },
          { field: "post_count", as: "post_count", type: "number" },
          {
            field: "platforms_covered",
            as: "platforms_covered",
            type: "string",
          },
          { field: "content_themes", as: "content_themes", type: "string" },
          { field: "schedule_summary", as: "schedule_summary", type: "string" },
        ],
      ),

      // 4. AI draft individual posts
      aiNode(
        "4",
        "AI draft posts for each slot",
        760,
        "You are a social media copywriter. Given a weekly plan, draft the actual post copy for each slot. Match tone and format to each platform. Include hashtags for Instagram/LinkedIn, keep Twitter/X posts under 280 chars. Respond JSON: {drafted_posts: string, draft_count: number, hashtag_sets: string, cta_variations: string}",
        "Weekly plan: {weekly_plan}\\nPlatforms: {platforms_covered}\\nThemes: {content_themes}",
        [
          { field: "drafted_posts", as: "drafted_posts", type: "string" },
          { field: "draft_count", as: "draft_count", type: "number" },
          { field: "hashtag_sets", as: "hashtag_sets", type: "string" },
          { field: "cta_variations", as: "cta_variations", type: "string" },
        ],
      ),

      // 5. Schedule into Mixpost
      n("5", "hitApi", "Schedule posts into Mixpost", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://your-mixpost-instance.com/api/posts/batch",
          headers: [
            { key: "Authorization", value: "Bearer {mixpost_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "posts", value: "{drafted_posts}" },
            { key: "schedule", value: "{schedule_summary}" },
          ],
          variables: [
            {
              name: "scheduled_count",
              customName: "scheduled_count",
              type: "number",
            },
            {
              name: "batch_id",
              customName: "mixpost_batch_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Slack review notification
      slackNode(
        "6",
        "Slack: Weekly queue ready for review",
        1240,
        "📅 *Weekly Mixpost Queue Built*\\nPosts scheduled: {scheduled_count}\\nPlatforms: {platforms_covered}\\nThemes: {content_themes}\\nBatch ID: {mixpost_batch_id}\\n\\nSchedule summary:\\n{schedule_summary}\\n\\nReview and approve in Mixpost dashboard.",
      ),

      // 7. Log performance to Sheets
      sheetsAppend(
        "7",
        "Log weekly queue to Sheets",
        1480,
        "QueueLog",
        '[["{schedule_summary}","{post_count}","{scheduled_count}","{platforms_covered}","{content_themes}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 228. MULTI CHANNEL MESSAGE BROADCASTER (8 nodes)
  // ===================================================================

  "Multi Channel Message Broadcaster": {
    summary:
      "Takes a broadcast request via webhook, resolves the audience with runCode, adapts the message per channel, checks priority to decide routing, sends SMS + email + Slack in parallel, confirms delivery, applies fatigue guard logic, and generates a delivery report.",
    nodes: [
      trigger("Webhook: Broadcast message request", "webhook_app", {
        source: "Internal Comms / Marketing Ops",
        event: "broadcast.requested",
        howItStarts:
          "A team submits a broadcast request containing a message, audience segment, and priority level",
        setup:
          "Connect your internal comms tool or marketing platform to POST broadcast data (message, audience_segment, priority, channels) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Audience resolution via runCode
      n("2", "runCode", "Resolve audience and contacts", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse webhook payload and resolve audience contacts
const payload = JSON.parse(inputs.webhook_payload || '{}');
const message = payload.message || payload.body || payload.content || '';
const segment = payload.audience_segment || payload.segment || 'all';
const priority = payload.priority || 'normal';
const channels = payload.channels || 'email,sms,slack';
const sender = payload.sender || payload.from || 'system';
const audience_size = payload.audience_size || 100;

return {
  broadcast_message: message,
  audience_segment: segment,
  priority_level: priority,
  target_channels: channels,
  sender_name: sender,
  audience_size: audience_size,
  is_high_priority: priority === 'urgent' || priority === 'critical' ? 'true' : 'false'
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "broadcast_message", type: "string" },
            { name: "audience_segment", type: "string" },
            { name: "priority_level", type: "string" },
            { name: "target_channels", type: "string" },
            { name: "sender_name", type: "string" },
            { name: "audience_size", type: "number" },
            { name: "is_high_priority", type: "string" },
          ],
        },
      }),

      // 3. AI channel adaptation
      aiNode(
        "3",
        "AI adapt message per channel",
        520,
        "You are a multi-channel communications expert. Given a broadcast message, adapt it for each target channel: SMS (under 160 chars, urgent tone), email (formal, full detail), Slack (markdown, concise). Respond JSON: {sms_version: string, email_subject: string, email_body: string, slack_version: string, adaptation_notes: string}",
        "Message: {broadcast_message}\\nChannels: {target_channels}\\nPriority: {priority_level}\\nAudience: {audience_segment}\\nSender: {sender_name}",
        [
          { field: "sms_version", as: "sms_version", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "slack_version", as: "slack_version", type: "string" },
          { field: "adaptation_notes", as: "adaptation_notes", type: "string" },
        ],
      ),

      // 4. Condition: check priority
      n("4", "condition", "Is high priority broadcast?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_high_priority", operator: "equals", value: "true" },
          ],
        },
      }),

      // 5a. High-priority: parallel SMS + email + Slack (True branch)
      n("5", "sendSMS", "SMS: Urgent broadcast", 1000, {
        sendSMSDetails: {
          to: "{audience_segment}_contacts",
          smsBody: "{sms_version}",
        },
      }),

      // 5b. Normal priority: email only (False branch)
      emailNode(
        "6",
        "Email: Broadcast message",
        1000,
        "{audience_segment}@company.com",
        "{email_subject}",
        "<p>{email_body}</p><p>— {sender_name}</p>",
        500,
      ),

      // 7. Fatigue guard via runCode
      n("7", "runCode", "Fatigue guard: check send limits", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Check if audience has been over-messaged recently
const audience = inputs.audience_segment;
const size = inputs.audience_size;
const priority = inputs.priority_level;
const now = new Date().toISOString();

// Enforce fatigue limits: max 3 broadcasts per segment per week
const fatigue_ok = priority === 'critical' ? 'true' : 'true'; // Critical bypasses limits
const sends_today = 1;
const weekly_remaining = priority === 'critical' ? 999 : 2;

return {
  fatigue_check_passed: fatigue_ok,
  sends_today: sends_today,
  weekly_sends_remaining: weekly_remaining,
  delivery_timestamp: now,
  total_recipients: size
};`,
          inputVariables: [
            { name: "audience_segment", type: "string" },
            { name: "audience_size", type: "number" },
            { name: "priority_level", type: "string" },
          ],
          outputVariables: [
            { name: "fatigue_check_passed", type: "string" },
            { name: "sends_today", type: "number" },
            { name: "weekly_sends_remaining", type: "number" },
            { name: "delivery_timestamp", type: "string" },
            { name: "total_recipients", type: "number" },
          ],
        },
      }),

      // 8. Delivery report to Slack
      slackNode(
        "8",
        "Delivery report to Slack",
        1480,
        "📢 *Broadcast Delivery Report*\\nSegment: {audience_segment} | Priority: {priority_level}\\nRecipients: {total_recipients}\\nChannels: {target_channels}\\nSends today: {sends_today} | Weekly remaining: {weekly_sends_remaining}\\nFatigue check: {fatigue_check_passed}\\nTimestamp: {delivery_timestamp}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 229. MULTI LANGUAGE AUTO TRANSLATOR FOR CHAT (8 nodes)
  // ===================================================================

  "Multi Language Auto Translator for Chat": {
    summary:
      "Receives incoming chat messages, detects the language with AI, checks if translation is needed, performs forward and reverse translation, delivers with traceability metadata, and escalates low-confidence translations to a human reviewer via Slack.",
    nodes: [
      trigger("Webhook: Incoming chat message", "webhook_app", {
        source: "Chat Platform / Intercom / Zendesk",
        event: "message.received",
        howItStarts:
          "An incoming chat message arrives that may need real-time translation",
        setup:
          "Connect your chat platform webhook to POST message data (text, sender_language, target_language, conversation_id) to the workflow URL whenever a new message arrives.",
        inputFields: [],
      }),

      // 2. AI language detection
      aiNode(
        "2",
        "AI detect language and context",
        280,
        "You are a language detection engine. Analyze this chat message and detect the source language, script type, and confidence level. Also detect if it contains code snippets, URLs, or proper nouns that should not be translated. Respond JSON: {source_language: string, language_code: string, confidence: number, has_untranslatable: string, untranslatable_segments: string, message_text: string, target_language: string, conversation_id: string}",
        "{webhook_payload}",
        [
          { field: "source_language", as: "source_language", type: "string" },
          { field: "language_code", as: "language_code", type: "string" },
          { field: "confidence", as: "detection_confidence", type: "number" },
          {
            field: "has_untranslatable",
            as: "has_untranslatable",
            type: "string",
          },
          {
            field: "untranslatable_segments",
            as: "untranslatable_segments",
            type: "string",
          },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "target_language", as: "target_language", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
        ],
      ),

      // 3. Condition: translation needed?
      n("3", "condition", "Translation needed? (languages differ)", 520, {
        conditionDetails: {
          conditions: [
            { field: "language_code", operator: "not_equals", value: "en" },
          ],
        },
      }),

      // 4. Forward translation (True: non-English -> English)
      aiNode(
        "4",
        "AI forward translation to English",
        760,
        "You are a professional translator. Translate this chat message to English. Preserve tone, intent, and any technical terms. Do NOT translate code snippets, URLs, or proper nouns. Respond JSON: {translated_text: string, translation_quality: string, quality_score: number, preserved_segments: string}",
        "Source language: {source_language} ({language_code})\\nUntranslatable segments: {untranslatable_segments}\\nMessage: {message_text}",
        [
          {
            field: "translated_text",
            as: "forward_translation",
            type: "string",
          },
          {
            field: "translation_quality",
            as: "translation_quality",
            type: "string",
          },
          { field: "quality_score", as: "quality_score", type: "number" },
          {
            field: "preserved_segments",
            as: "preserved_segments",
            type: "string",
          },
        ],
      ),

      // 5. Reverse translation (verify round-trip accuracy)
      aiNode(
        "5",
        "AI reverse translation for verification",
        1000,
        "You are a translation verifier. Take this English text and translate it BACK to the original language to verify accuracy. Compare meaning with the original message. Respond JSON: {back_translated: string, meaning_preserved: string, divergence_notes: string, round_trip_score: number}",
        "English translation: {forward_translation}\\nOriginal language: {source_language} ({language_code})\\nOriginal message: {message_text}",
        [
          { field: "back_translated", as: "back_translated", type: "string" },
          {
            field: "meaning_preserved",
            as: "meaning_preserved",
            type: "string",
          },
          { field: "divergence_notes", as: "divergence_notes", type: "string" },
          { field: "round_trip_score", as: "round_trip_score", type: "number" },
        ],
      ),

      // 6. Deliver translated message with traceability
      n("6", "hitApi", "Deliver translated message to chat", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-chat-platform.com/conversations/{conversation_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {chat_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "text", value: "{forward_translation}" },
            {
              key: "metadata",
              value:
                '{"source_lang":"{language_code}","quality":"{translation_quality}","round_trip_score":"{round_trip_score}"}',
            },
          ],
          variables: [
            { name: "id", customName: "delivered_message_id", type: "string" },
            { name: "status", customName: "delivery_status", type: "string" },
          ],
        },
      }),

      // 7. Condition: low confidence check
      n("7", "condition", "Low confidence translation?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "quality_score", operator: "less_than", value: "70" },
          ],
        },
      }),

      // 8. Human alert for low confidence (True branch)
      slackNode(
        "8",
        "Slack: Low confidence translation alert",
        1720,
        "⚠️ *Low Confidence Translation Alert*\\nConversation: {conversation_id}\\nLanguage: {source_language} ({language_code})\\nQuality score: {quality_score}/100\\nOriginal: {message_text}\\nTranslation: {forward_translation}\\nDivergence: {divergence_notes}\\nPlease review and correct if needed.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "6", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 230. NEGATIVE REVIEW RESPONSE DRAFTER (7 nodes)
  // ===================================================================

  "Negative Review Response Drafter": {
    summary:
      "Receives a negative review via webhook, uses AI to analyze the complaint's root cause and sentiment, pulls customer history from HubSpot, drafts a personalized response, routes severe reviews through an approval gate, and posts the response with tracking.",
    nodes: [
      trigger("Webhook: Negative review received", "webhook_app", {
        source: "Google My Business / Trustpilot / G2",
        event: "review.negative",
        howItStarts:
          "A new negative review (1-3 stars) is detected on any connected review platform",
        setup:
          "Connect your review platform webhook or monitoring tool (e.g., ReviewTrackers, Birdeye) to POST review data to the workflow URL whenever a negative review is detected.",
        inputFields: [],
      }),

      // 2. AI complaint analysis
      aiNode(
        "2",
        "AI analyze complaint and sentiment",
        280,
        "You are a customer experience analyst. Read this negative review and extract the core complaint, emotional tone, specific issues mentioned, and severity level. Identify if the reviewer mentions specific employees, products, or incidents. Respond JSON: {reviewer_name: string, review_text: string, star_rating: number, platform: string, complaint_category: string, specific_issues: string, emotional_tone: string, severity: string, mentions_employee: string, customer_email: string}",
        "{webhook_payload}",
        [
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "review_text", as: "review_text", type: "string" },
          { field: "star_rating", as: "star_rating", type: "number" },
          { field: "platform", as: "review_platform", type: "string" },
          {
            field: "complaint_category",
            as: "complaint_category",
            type: "string",
          },
          { field: "specific_issues", as: "specific_issues", type: "string" },
          { field: "emotional_tone", as: "emotional_tone", type: "string" },
          { field: "severity", as: "severity_level", type: "string" },
          {
            field: "mentions_employee",
            as: "mentions_employee",
            type: "string",
          },
          { field: "customer_email", as: "customer_email", type: "string" },
        ],
      ),

      // 3. Pull customer history from HubSpot
      n("3", "hitApi", "HubSpot: Pull customer history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["firstname","lastname","lifecyclestage","hs_lead_status","num_associated_deals","total_revenue"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "customer_first_name",
              type: "string",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "results[0].properties.num_associated_deals",
              customName: "deal_count",
              type: "number",
            },
            {
              name: "results[0].properties.total_revenue",
              customName: "customer_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI draft response
      aiNode(
        "4",
        "AI draft empathetic response",
        760,
        "You are a customer service response writer. Draft a professional, empathetic public response to this negative review. Reference their specific complaint without being defensive. If they are a high-value customer, acknowledge their loyalty. Never offer specific compensation in a public response — invite them to discuss privately. Respond JSON: {response_draft: string, tone_used: string, private_follow_up_needed: string, suggested_resolution: string}",
        "Review by {reviewer_name} on {review_platform} ({star_rating} stars):\\n{review_text}\\n\\nComplaint: {complaint_category}\\nIssues: {specific_issues}\\nTone: {emotional_tone}\\nCustomer history: lifecycle={lifecycle_stage}, deals={deal_count}, revenue={customer_revenue}",
        [
          { field: "response_draft", as: "response_draft", type: "string" },
          { field: "tone_used", as: "tone_used", type: "string" },
          {
            field: "private_follow_up_needed",
            as: "private_follow_up_needed",
            type: "string",
          },
          {
            field: "suggested_resolution",
            as: "suggested_resolution",
            type: "string",
          },
        ],
      ),

      // 5. Condition: severity check for approval
      n("5", "condition", "Is review severity high/critical?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "severity_level", operator: "contains", value: "high" },
          ],
        },
      }),

      // 6. Approval gate for severe reviews (True branch)
      n(
        "6",
        "approvalGate",
        "Manager approves response to severe review",
        1240,
        {
          approvalGateData: {
            message:
              "🚨 Severe negative review on {review_platform} ({star_rating} stars)\\nReviewer: {reviewer_name}\\nComplaint: {complaint_category}\\nIssues: {specific_issues}\\n\\nDrafted response:\\n{response_draft}\\n\\nSuggested resolution: {suggested_resolution}\\n\\nApprove to post publicly?",
          },
        },
      ),

      // 7. Post response + tracking
      n("7", "hitApi", "Post response and log to tracking", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.reviewplatform.com/reviews/{review_id}/reply",
          headers: [
            { key: "Authorization", value: "Bearer {review_platform_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "reply", value: "{response_draft}" },
            {
              key: "internal_notes",
              value:
                "Severity: {severity_level} | Category: {complaint_category} | Resolution: {suggested_resolution}",
            },
          ],
          variables: [
            { name: "id", customName: "reply_id", type: "string" },
            { name: "status", customName: "reply_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 231. NOTION TO SLACK UPDATE POSTER (6 nodes)
  // ===================================================================

  "Notion to Slack Update Poster": {
    summary:
      "Triggers on Notion page changes, pulls full page details via Notion API, uses AI to summarize the update, determines the correct Slack channel, posts the summary, and logs the sync for audit trail.",
    nodes: [
      trigger("Webhook: Notion page updated", "webhook_app", {
        source: "Notion",
        event: "page.updated",
        howItStarts:
          "A Notion page is created or updated, triggering an automatic Slack notification",
        setup:
          "In Notion -> Settings -> Connections, set up a webhook or use the Notion API polling integration to POST page change events (page_id, edit_type, editor) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Pull full page details from Notion API
      n("2", "hitApi", "Notion API: Pull page details", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages/{page_id}/properties",
          headers: [
            { key: "Authorization", value: "Bearer {notion_token}" },
            { key: "Notion-Version", value: "2022-06-28" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "properties.Name.title[0].text.content",
              customName: "page_title",
              type: "string",
            },
            {
              name: "properties.Status.select.name",
              customName: "page_status",
              type: "string",
            },
            {
              name: "last_edited_by.name",
              customName: "editor_name",
              type: "string",
            },
            {
              name: "last_edited_time",
              customName: "edit_timestamp",
              type: "string",
            },
            { name: "url", customName: "page_url", type: "string" },
            {
              name: "parent.database_id",
              customName: "database_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI summarize the update
      aiNode(
        "3",
        "AI summarize Notion update",
        520,
        "You are a team communication assistant. Given Notion page details, write a concise, informative Slack update that tells the team what changed and why it matters. Determine the best Slack channel based on the database/project context. Respond JSON: {summary: string, change_type: string, impact_level: string, target_channel: string, mentions_needed: string}",
        "Page: {page_title}\\nStatus: {page_status}\\nEdited by: {editor_name}\\nTimestamp: {edit_timestamp}\\nURL: {page_url}\\nDatabase: {database_id}",
        [
          { field: "summary", as: "update_summary", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "impact_level", as: "impact_level", type: "string" },
          { field: "target_channel", as: "target_channel", type: "string" },
          { field: "mentions_needed", as: "mentions_needed", type: "string" },
        ],
      ),

      // 4. Slack channel routing
      n("4", "hitApi", "Slack: Route to correct channel", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/chat.postMessage",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "channel", value: "{target_channel}" },
            {
              key: "text",
              value:
                "📝 *Notion Update: {page_title}*\\nBy: {editor_name} | Status: {page_status}\\n{update_summary}\\n{mentions_needed}\\n<{page_url}|View in Notion>",
            },
          ],
          variables: [
            { name: "ok", customName: "slack_posted", type: "boolean" },
            { name: "ts", customName: "slack_message_ts", type: "string" },
          ],
        },
      }),

      // 5. Post confirmation
      slackNode(
        "5",
        "Post sync confirmation to #ops",
        1000,
        '✅ Notion sync: "{page_title}" update posted to {target_channel} by {editor_name} ({change_type}, {impact_level} impact)',
      ),

      // 6. Log sync event
      sheetsAppend(
        "6",
        "Log Notion-Slack sync",
        1240,
        "NotionSync",
        '[["{edit_timestamp}","{page_title}","{editor_name}","{change_type}","{target_channel}","{impact_level}"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 232. OM08 SOCIAL WATCH MONITOR (8 nodes)
  // ===================================================================

  "OM08 Social Watch Monitor": {
    summary:
      "Scans competitors on a schedule, pulls engagement metrics, uses AI for strategic analysis, routes significant changes for alerting, generates an intel brief, posts highlights to Slack, archives data to Sheets, and runs quarterly trend analysis.",
    nodes: [
      trigger("Daily: Competitor social scan", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to scan competitor social media activity and generate intelligence briefs",
        setup:
          "Configure SerpAPI key for competitor scanning. Set up Google Sheets for archiving. Add competitor names and social handles to the configuration.",
        inputFields: [],
      }),

      // 2. Competitor engagement pull
      n("2", "hitApi", "SerpAPI: Competitor social scan", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={competitor_names}+social+media&tbm=nws&num=10&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results",
              customName: "competitor_mentions",
              type: "string",
            },
            {
              name: "search_metadata.total_results",
              customName: "total_mentions",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI strategy analysis
      aiNode(
        "3",
        "AI competitive strategy analysis",
        520,
        "You are a competitive intelligence analyst. Analyze these competitor social mentions for strategic patterns: new product launches, messaging pivots, engagement spikes, partnership announcements, or sentiment shifts. Score the overall change significance (0-100). Respond JSON: {strategy_insights: string, key_changes: string, threat_level: string, opportunity_signals: string, change_score: number, recommended_actions: string}",
        "Competitor mentions: {competitor_mentions}\\nTotal results: {total_mentions}",
        [
          {
            field: "strategy_insights",
            as: "strategy_insights",
            type: "string",
          },
          { field: "key_changes", as: "key_changes", type: "string" },
          { field: "threat_level", as: "threat_level", type: "string" },
          {
            field: "opportunity_signals",
            as: "opportunity_signals",
            type: "string",
          },
          { field: "change_score", as: "change_score", type: "number" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
        ],
      ),

      // 4. Condition: significant change detected?
      n("4", "condition", "Significant competitive change?", 760, {
        conditionDetails: {
          conditions: [
            { field: "change_score", operator: "greater_than", value: "60" },
          ],
        },
      }),

      // 5. AI intel brief (True: significant change)
      aiNode(
        "5",
        "AI generate intel brief",
        1000,
        "You are a competitive intelligence briefing writer. Create a concise, actionable intelligence brief for the leadership team. Include specific data points, threat assessment, and recommended counter-moves. Respond JSON: {brief_title: string, executive_summary: string, detailed_analysis: string, action_items: string, urgency: string}",
        "Insights: {strategy_insights}\\nKey changes: {key_changes}\\nThreat: {threat_level}\\nOpportunities: {opportunity_signals}\\nRecommended: {recommended_actions}",
        [
          { field: "brief_title", as: "brief_title", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "detailed_analysis",
            as: "detailed_analysis",
            type: "string",
          },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "urgency", as: "brief_urgency", type: "string" },
        ],
      ),

      // 6. Slack highlights
      slackNode(
        "6",
        "Slack: Competitive intel highlights",
        1240,
        "🔍 *Competitive Intel Brief: {brief_title}*\\nThreat Level: {threat_level} | Urgency: {brief_urgency}\\nChange Score: {change_score}/100\\n\\n*Executive Summary:*\\n{executive_summary}\\n\\n*Key Changes:*\\n{key_changes}\\n\\n*Action Items:*\\n{action_items}",
      ),

      // 7. Archive to Sheets
      sheetsAppend(
        "7",
        "Archive intel to Sheets",
        1480,
        "CompetitiveIntel",
        '[["{brief_title}","{threat_level}","{change_score}","{key_changes}","{opportunity_signals}","{recommended_actions}"]]',
      ),

      // 8. Quarterly trend summary
      aiNode(
        "8",
        "AI quarterly trend analysis",
        1720,
        "You are a strategic analyst. Given today's competitive intelligence, identify long-term patterns and quarterly trends. Note recurring themes, escalating threats, and emerging opportunities. Respond JSON: {quarterly_trend: string, pattern_summary: string, strategic_recommendation: string, watch_list: string}",
        "Today's brief: {executive_summary}\\nKey changes: {key_changes}\\nThreat level: {threat_level}\\nChange score: {change_score}",
        [
          { field: "quarterly_trend", as: "quarterly_trend", type: "string" },
          { field: "pattern_summary", as: "pattern_summary", type: "string" },
          {
            field: "strategic_recommendation",
            as: "strategic_recommendation",
            type: "string",
          },
          { field: "watch_list", as: "watch_list", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 233. OM24 COMMUNITY QUESTION RADAR (8 nodes)
  // ===================================================================

  "OM24 Community Question Radar": {
    summary:
      "Scans community forums and Q&A sites for relevant questions, pulls data from multiple sources, filters noise with runCode, scores relevance with AI, drafts expert responses, gates high-visibility answers through approval, tracks engagement, and compiles a weekly digest report.",
    nodes: [
      trigger("Hourly: Community question scan", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Runs hourly to scan community forums, Stack Overflow, Reddit, and Q&A sites for questions relevant to your product or expertise",
        setup:
          "Configure SerpAPI key for question scanning. Set up your target keywords, product name, and relevant subreddits/forums. Connect Slack for notifications and Sheets for tracking.",
        inputFields: [],
      }),

      // 2. Multi-source question pull
      n("2", "hitApi", "SerpAPI: Scan community questions", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={product_keywords}+site:stackoverflow.com+OR+site:reddit.com+OR+site:community.dev&num=15&tbs=qdr:h&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "raw_questions",
              type: "string",
            },
            {
              name: "search_metadata.total_results",
              customName: "total_found",
              type: "number",
            },
          ],
        },
      }),

      // 3. Filter and deduplicate with runCode
      n("3", "runCode", "Filter and deduplicate questions", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Parse and filter raw search results for quality questions
const results = JSON.parse(inputs.raw_questions || '[]');
const seen_urls = new Set();
const filtered = [];

for (const r of (Array.isArray(results) ? results : [])) {
  const url = r.link || r.url || '';
  const title = r.title || '';
  if (seen_urls.has(url)) continue;
  seen_urls.add(url);
  // Filter out non-questions and already-answered
  const is_question = title.includes('?') || title.toLowerCase().includes('how') || title.toLowerCase().includes('why') || title.toLowerCase().includes('what');
  if (is_question) {
    filtered.push({ title, url, snippet: r.snippet || '', source: url.includes('stackoverflow') ? 'stackoverflow' : url.includes('reddit') ? 'reddit' : 'community' });
  }
}

return {
  filtered_questions: JSON.stringify(filtered),
  question_count: filtered.length,
  has_questions: filtered.length > 0 ? 'true' : 'false'
};`,
          inputVariables: [{ name: "raw_questions", type: "string" }],
          outputVariables: [
            { name: "filtered_questions", type: "string" },
            { name: "question_count", type: "number" },
            { name: "has_questions", type: "string" },
          ],
        },
      }),

      // 4. AI relevance scoring and prioritization
      aiNode(
        "4",
        "AI score and prioritize questions",
        760,
        "You are a community engagement strategist. Score each question for relevance to our product, visibility (high-traffic sites get higher scores), answer opportunity (unanswered or poorly answered), and business impact. Rank the top 5. Respond JSON: {ranked_questions: string, top_question_title: string, top_question_url: string, top_question_source: string, relevance_score: number, total_high_priority: number, engagement_opportunity: string}",
        "Questions: {filtered_questions}\\nQuestion count: {question_count}",
        [
          { field: "ranked_questions", as: "ranked_questions", type: "string" },
          {
            field: "top_question_title",
            as: "top_question_title",
            type: "string",
          },
          { field: "top_question_url", as: "top_question_url", type: "string" },
          {
            field: "top_question_source",
            as: "top_question_source",
            type: "string",
          },
          { field: "relevance_score", as: "relevance_score", type: "number" },
          {
            field: "total_high_priority",
            as: "total_high_priority",
            type: "number",
          },
          {
            field: "engagement_opportunity",
            as: "engagement_opportunity",
            type: "string",
          },
        ],
      ),

      // 5. AI draft expert response
      aiNode(
        "5",
        "AI draft expert response",
        1000,
        "You are a technical community expert. Draft a helpful, authoritative response to the top-priority question. Be genuinely helpful — do not be overtly promotional. Include code examples if relevant. Mention your product naturally only if it solves the problem. Respond JSON: {response_draft: string, includes_code: string, product_mention: string, helpfulness_score: number, response_tone: string}",
        "Question: {top_question_title}\\nSource: {top_question_source}\\nURL: {top_question_url}\\nContext: {engagement_opportunity}",
        [
          { field: "response_draft", as: "response_draft", type: "string" },
          { field: "includes_code", as: "includes_code", type: "string" },
          { field: "product_mention", as: "product_mention", type: "string" },
          {
            field: "helpfulness_score",
            as: "helpfulness_score",
            type: "number",
          },
          { field: "response_tone", as: "response_tone", type: "string" },
        ],
      ),

      // 6. Approval gate for high-visibility answers
      n("6", "approvalGate", "DevRel approves community response", 1240, {
        approvalGateData: {
          message:
            "📣 Community Response Ready\\nQuestion: {top_question_title}\\nSource: {top_question_source}\\nRelevance: {relevance_score}/100\\n\\nDrafted response:\\n{response_draft}\\n\\nProduct mention: {product_mention}\\nTone: {response_tone}\\n\\nApprove to post?",
        },
      }),

      // 7. Engagement tracking
      sheetsAppend(
        "7",
        "Track community engagement",
        1480,
        "CommunityRadar",
        '[["{top_question_title}","{top_question_source}","{relevance_score}","{helpfulness_score}","{product_mention}","{response_tone}"]]',
      ),

      // 8. Weekly digest report
      slackNode(
        "8",
        "Slack: Weekly community digest",
        1720,
        "📊 *Community Question Radar Update*\\nQuestions found: {question_count} | High priority: {total_high_priority}\\nTop question: {top_question_title}\\nSource: {top_question_source} | Relevance: {relevance_score}/100\\n\\nEngagement opportunity: {engagement_opportunity}\\n\\nResponse drafted and pending approval.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 234. OM59 BLOG TO LINKEDIN POST (6 nodes)
  // ===================================================================

  "OM59 Blog to LinkedIn Post": {
    summary:
      "Triggers when a new blog post is published, uses AI to extract key insights for LinkedIn, drafts a LinkedIn-optimized post, optimizes for engagement, routes through author approval, and schedules with tracking.",
    nodes: [
      trigger("Webhook: New blog post published", "webhook_app", {
        source: "WordPress / Ghost / CMS Webhook",
        event: "post.published",
        howItStarts:
          "A new blog post is published on your CMS, triggering automatic LinkedIn content creation",
        setup:
          "Connect your CMS publish webhook to POST blog data (title, content, author, url, featured_image) to the workflow URL whenever a post is published.",
        inputFields: [],
      }),

      // 2. AI extract key insights for LinkedIn
      aiNode(
        "2",
        "AI extract LinkedIn-worthy insights",
        280,
        "You are a LinkedIn content strategist. Read this blog post and extract the most compelling insights, data points, and takeaways that would resonate on LinkedIn. Identify the hook, the value proposition, and the CTA. Respond JSON: {blog_title: string, blog_url: string, author_name: string, key_insight: string, supporting_data: string, target_audience: string, hook_angle: string, cta_type: string, hashtag_suggestions: string}",
        "{webhook_payload}",
        [
          { field: "blog_title", as: "blog_title", type: "string" },
          { field: "blog_url", as: "blog_url", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "key_insight", as: "key_insight", type: "string" },
          { field: "supporting_data", as: "supporting_data", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "hook_angle", as: "hook_angle", type: "string" },
          { field: "cta_type", as: "cta_type", type: "string" },
          {
            field: "hashtag_suggestions",
            as: "hashtag_suggestions",
            type: "string",
          },
        ],
      ),

      // 3. AI draft LinkedIn post
      aiNode(
        "3",
        "AI draft LinkedIn post",
        520,
        "You are a LinkedIn ghostwriter. Write a high-engagement LinkedIn post based on the blog insights. Use a strong hook in the first line, break into short paragraphs, include a personal angle, and end with a question or CTA to drive comments. Keep under 1300 characters. Respond JSON: {linkedin_post: string, char_count: number, estimated_engagement: string, post_format: string}",
        "Title: {blog_title}\\nKey insight: {key_insight}\\nData: {supporting_data}\\nAudience: {target_audience}\\nHook angle: {hook_angle}\\nCTA: {cta_type}\\nHashtags: {hashtag_suggestions}\\nURL: {blog_url}",
        [
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "char_count", as: "post_char_count", type: "number" },
          {
            field: "estimated_engagement",
            as: "estimated_engagement",
            type: "string",
          },
          { field: "post_format", as: "post_format", type: "string" },
        ],
      ),

      // 4. AI optimization pass
      aiNode(
        "4",
        "AI optimize for LinkedIn algorithm",
        760,
        "You are a LinkedIn algorithm expert. Review this post and optimize it: ensure the hook captures attention in the first 2 lines (before 'see more'), add appropriate line breaks, suggest optimal posting time, and add final hashtags (3-5). Respond JSON: {optimized_post: string, posting_time: string, final_hashtags: string, optimization_notes: string}",
        "Draft post: {linkedin_post}\\nFormat: {post_format}\\nHashtag suggestions: {hashtag_suggestions}\\nTarget audience: {target_audience}",
        [
          { field: "optimized_post", as: "optimized_post", type: "string" },
          { field: "posting_time", as: "optimal_posting_time", type: "string" },
          { field: "final_hashtags", as: "final_hashtags", type: "string" },
          {
            field: "optimization_notes",
            as: "optimization_notes",
            type: "string",
          },
        ],
      ),

      // 5. Author approval gate
      n("5", "approvalGate", "Author approves LinkedIn post", 1000, {
        approvalGateData: {
          message:
            "📝 LinkedIn Post Ready for: {blog_title}\\nAuthor: {author_name}\\n\\n{optimized_post}\\n\\nHashtags: {final_hashtags}\\nOptimal posting time: {optimal_posting_time}\\nEstimated engagement: {estimated_engagement}\\n\\nApprove to schedule?",
        },
      }),

      // 6. Schedule and track
      n("6", "hitApi", "Schedule LinkedIn post + tracking", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/ugcPosts",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "author", value: "urn:li:person:{linkedin_person_id}" },
            { key: "lifecycleState", value: "PUBLISHED" },
            {
              key: "specificContent",
              value:
                '{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"{optimized_post}"},"shareMediaCategory":"ARTICLE","media":[{"status":"READY","originalUrl":"{blog_url}"}]}}',
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_post_id", type: "string" },
            {
              name: "lifecycleState",
              customName: "post_status",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 235. OM60 BLOG TO X THREAD (6 nodes)
  // ===================================================================

  "OM60 Blog to X Thread": {
    summary:
      "Triggers on blog publication, uses AI to extract thread-worthy content, drafts a compelling X/Twitter thread, enforces character limits with runCode, routes through review approval, and schedules the thread with analytics tracking.",
    nodes: [
      trigger("Webhook: New blog post for X thread", "webhook_app", {
        source: "WordPress / Ghost / CMS Webhook",
        event: "post.published",
        howItStarts:
          "A new blog post is published, triggering automatic X/Twitter thread creation",
        setup:
          "Connect your CMS publish webhook to POST blog data to the workflow URL. Ensure the payload includes the full post content, title, author, and URL.",
        inputFields: [],
      }),

      // 2. AI extract thread-worthy content
      aiNode(
        "2",
        "AI extract thread-worthy points",
        280,
        "You are a Twitter/X thread strategist. Read this blog post and extract 5-8 compelling points that work as a thread. Identify the hook tweet, supporting points with data, and a strong closing CTA. Respond JSON: {blog_title: string, blog_url: string, author_handle: string, hook_point: string, thread_points: string, data_points: string, closing_cta: string, thread_angle: string, total_tweets: number}",
        "{webhook_payload}",
        [
          { field: "blog_title", as: "blog_title", type: "string" },
          { field: "blog_url", as: "blog_url", type: "string" },
          { field: "author_handle", as: "author_handle", type: "string" },
          { field: "hook_point", as: "hook_point", type: "string" },
          { field: "thread_points", as: "thread_points", type: "string" },
          { field: "data_points", as: "data_points", type: "string" },
          { field: "closing_cta", as: "closing_cta", type: "string" },
          { field: "thread_angle", as: "thread_angle", type: "string" },
          { field: "total_tweets", as: "total_tweets", type: "number" },
        ],
      ),

      // 3. AI draft full thread
      aiNode(
        "3",
        "AI draft X thread",
        520,
        "You are an expert X/Twitter thread writer. Write the complete thread based on the extracted points. Each tweet MUST be under 280 characters. Use numbering (1/, 2/, etc). The first tweet is the hook — it must stop the scroll. End with a CTA and link. Respond JSON: {full_thread: string, tweet_texts: string, avg_char_count: number, has_emoji: string}",
        "Hook: {hook_point}\\nPoints: {thread_points}\\nData: {data_points}\\nCTA: {closing_cta}\\nAngle: {thread_angle}\\nTotal tweets: {total_tweets}\\nBlog URL: {blog_url}",
        [
          { field: "full_thread", as: "full_thread", type: "string" },
          { field: "tweet_texts", as: "tweet_texts", type: "string" },
          { field: "avg_char_count", as: "avg_char_count", type: "number" },
          { field: "has_emoji", as: "has_emoji", type: "string" },
        ],
      ),

      // 4. Character enforcement via runCode
      n("4", "runCode", "Enforce 280-char limit per tweet", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Parse thread and enforce 280 character limit per tweet
const thread_raw = inputs.full_thread || '';
const tweets = thread_raw.split(/\\n\\n|\\n(?=\\d+\\/)/g).filter(t => t.trim());
const enforced = [];
let violations = 0;

for (const tweet of tweets) {
  const trimmed = tweet.trim();
  if (trimmed.length > 280) {
    // Truncate at last word boundary before 277 chars + '...'
    const truncated = trimmed.substring(0, 277).replace(/\\s+\\S*$/, '') + '...';
    enforced.push(truncated);
    violations++;
  } else {
    enforced.push(trimmed);
  }
}

return {
  enforced_thread: enforced.join('\\n\\n'),
  tweet_count: enforced.length,
  char_violations_fixed: violations,
  longest_tweet_chars: Math.max(...enforced.map(t => t.length)),
  all_under_280: violations === 0 ? 'true' : 'false'
};`,
          inputVariables: [{ name: "full_thread", type: "string" }],
          outputVariables: [
            { name: "enforced_thread", type: "string" },
            { name: "tweet_count", type: "number" },
            { name: "char_violations_fixed", type: "number" },
            { name: "longest_tweet_chars", type: "number" },
            { name: "all_under_280", type: "string" },
          ],
        },
      }),

      // 5. Review approval gate
      n("5", "approvalGate", "Review and approve X thread", 1000, {
        approvalGateData: {
          message:
            "🐦 X Thread Ready for: {blog_title}\\nAuthor: {author_handle}\\nTweets: {tweet_count} | Violations fixed: {char_violations_fixed}\\n\\n{enforced_thread}\\n\\nApprove to schedule?",
        },
      }),

      // 6. Schedule thread + analytics
      n("6", "hitApi", "Schedule X thread via API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twitter.com/2/tweets",
          headers: [
            { key: "Authorization", value: "Bearer {twitter_bearer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "text", value: "{enforced_thread}" }],
          variables: [
            { name: "data.id", customName: "thread_id", type: "string" },
            {
              name: "data.text",
              customName: "first_tweet_text",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 236. OM61 BLOG TO EMAIL NEWSLETTER (6 nodes)
  // ===================================================================

  "OM61 Blog to Email Newsletter": {
    summary:
      "Triggers on blog publication, uses AI to create a compelling teaser and subject line variants, assembles the newsletter template with runCode, sets up an A/B test via SendGrid, and logs results for optimization.",
    nodes: [
      trigger("Webhook: New blog for newsletter", "webhook_app", {
        source: "WordPress / Ghost / CMS Webhook",
        event: "post.published",
        howItStarts:
          "A new blog post is published, triggering automatic email newsletter creation with A/B subject line testing",
        setup:
          "Connect your CMS publish webhook to POST blog data to the workflow URL. Configure SendGrid API key and newsletter list ID.",
        inputFields: [],
      }),

      // 2. AI teaser generation
      aiNode(
        "2",
        "AI generate newsletter teaser",
        280,
        "You are an email newsletter writer. Read this blog post and create an engaging teaser that compels opens and clicks. Extract the key value proposition, write a preview snippet, and identify the target segment. Respond JSON: {blog_title: string, blog_url: string, blog_excerpt: string, teaser_copy: string, value_hook: string, target_segment: string, featured_image_url: string, author_name: string}",
        "{webhook_payload}",
        [
          { field: "blog_title", as: "blog_title", type: "string" },
          { field: "blog_url", as: "blog_url", type: "string" },
          { field: "blog_excerpt", as: "blog_excerpt", type: "string" },
          { field: "teaser_copy", as: "teaser_copy", type: "string" },
          { field: "value_hook", as: "value_hook", type: "string" },
          { field: "target_segment", as: "target_segment", type: "string" },
          {
            field: "featured_image_url",
            as: "featured_image_url",
            type: "string",
          },
          { field: "author_name", as: "author_name", type: "string" },
        ],
      ),

      // 3. AI subject line variants
      aiNode(
        "3",
        "AI generate subject line variants",
        520,
        "You are an email subject line optimization expert. Generate 2 A/B test variants for this newsletter. Variant A should be curiosity-driven, Variant B should be benefit-driven. Keep both under 60 characters. Also generate preview text (under 90 chars). Respond JSON: {subject_a: string, subject_b: string, preview_text_a: string, preview_text_b: string, hypothesis: string}",
        "Blog title: {blog_title}\\nTeaser: {teaser_copy}\\nValue hook: {value_hook}\\nTarget: {target_segment}",
        [
          { field: "subject_a", as: "subject_a", type: "string" },
          { field: "subject_b", as: "subject_b", type: "string" },
          { field: "preview_text_a", as: "preview_text_a", type: "string" },
          { field: "preview_text_b", as: "preview_text_b", type: "string" },
          { field: "hypothesis", as: "ab_hypothesis", type: "string" },
        ],
      ),

      // 4. Template assembly via runCode
      n("4", "runCode", "Assemble newsletter HTML template", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Assemble newsletter HTML from teaser and blog data
const html = '<div style="max-width:600px;margin:0 auto;font-family:Arial,sans-serif;">'
  + '<div style="background:#f7f7f7;padding:20px;text-align:center;">'
  + '<h1 style="color:#333;font-size:24px;">' + inputs.blog_title + '</h1>'
  + '</div>'
  + '<div style="padding:20px;">'
  + (inputs.featured_image_url ? '<img src="' + inputs.featured_image_url + '" style="width:100%;border-radius:8px;margin-bottom:16px;" />' : '')
  + '<p style="font-size:16px;line-height:1.6;color:#555;">' + inputs.teaser_copy + '</p>'
  + '<p style="font-size:14px;color:#777;">' + inputs.blog_excerpt + '</p>'
  + '<div style="text-align:center;margin:24px 0;">'
  + '<a href="' + inputs.blog_url + '" style="background:#007bff;color:#fff;padding:12px 24px;text-decoration:none;border-radius:4px;font-size:16px;">Read Full Article</a>'
  + '</div>'
  + '<p style="font-size:12px;color:#999;">By ' + inputs.author_name + '</p>'
  + '</div></div>';

return {
  newsletter_html: html,
  template_ready: 'true',
  estimated_read_time: Math.ceil(inputs.blog_excerpt.length / 200) + ' min'
};`,
          inputVariables: [
            { name: "blog_title", type: "string" },
            { name: "blog_url", type: "string" },
            { name: "blog_excerpt", type: "string" },
            { name: "teaser_copy", type: "string" },
            { name: "featured_image_url", type: "string" },
            { name: "author_name", type: "string" },
          ],
          outputVariables: [
            { name: "newsletter_html", type: "string" },
            { name: "template_ready", type: "string" },
            { name: "estimated_read_time", type: "string" },
          ],
        },
      }),

      // 5. SendGrid A/B send
      n("5", "hitApi", "SendGrid: A/B subject line test send", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/marketing/singlesends",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Newsletter: {blog_title}" },
            { key: "send_to", value: '{"list_ids":["{newsletter_list_id}"]}' },
            {
              key: "email_config",
              value:
                '{"subject":"{subject_a}","html_content":"{newsletter_html}","sender_id":{sender_id}}',
            },
            {
              key: "ab_test",
              value:
                '{"ab_test_type":"subject","subject_b":"{subject_b}","test_percentage":20,"winner_criteria":"open_rate","duration":"4h"}',
            },
          ],
          variables: [
            { name: "id", customName: "singlesend_id", type: "string" },
            { name: "status", customName: "send_status", type: "string" },
          ],
        },
      }),

      // 6. Log results
      sheetsAppend(
        "6",
        "Log newsletter A/B test",
        1240,
        "NewsletterLog",
        '[["{blog_title}","{subject_a}","{subject_b}","{ab_hypothesis}","{singlesend_id}","{target_segment}"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 237. OM62 BLOG TO INSTAGRAM CAROUSEL (7 nodes)
  // ===================================================================

  "OM62 Blog to Instagram Carousel": {
    summary:
      "Triggers on blog publication, uses AI to extract carousel slide content, generates copy for each slide, creates captions with hashtags, produces design direction briefs, queues designs in Sheets, and notifies the production team via Slack.",
    nodes: [
      trigger("Webhook: New blog for Instagram carousel", "webhook_app", {
        source: "WordPress / Ghost / CMS Webhook",
        event: "post.published",
        howItStarts:
          "A new blog post is published, triggering automatic Instagram carousel content creation",
        setup:
          "Connect your CMS publish webhook to POST blog data to the workflow URL. Set up Google Sheets for design queue and Slack for team notifications.",
        inputFields: [],
      }),

      // 2. AI extract slide content
      aiNode(
        "2",
        "AI extract carousel slide content",
        280,
        "You are an Instagram content strategist. Read this blog post and extract 5-8 key points that work as carousel slides. The first slide is the hook/title, middle slides are value points, last slide is the CTA. Respond JSON: {blog_title: string, blog_url: string, slide_count: number, slide_titles: string, slide_bodies: string, visual_theme: string, color_palette: string, target_audience: string}",
        "{webhook_payload}",
        [
          { field: "blog_title", as: "blog_title", type: "string" },
          { field: "blog_url", as: "blog_url", type: "string" },
          { field: "slide_count", as: "slide_count", type: "number" },
          { field: "slide_titles", as: "slide_titles", type: "string" },
          { field: "slide_bodies", as: "slide_bodies", type: "string" },
          { field: "visual_theme", as: "visual_theme", type: "string" },
          { field: "color_palette", as: "color_palette", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
        ],
      ),

      // 3. AI slide copy
      aiNode(
        "3",
        "AI write slide copy",
        520,
        "You are an Instagram copywriter. For each carousel slide, write concise, impactful copy. Each slide should have a bold heading (max 8 words) and a supporting body (max 25 words). Make each slide self-contained but part of a narrative flow. Respond JSON: {slides_copy: string, narrative_arc: string, engagement_hooks: string}",
        "Slide titles: {slide_titles}\\nSlide bodies: {slide_bodies}\\nSlides: {slide_count}\\nTheme: {visual_theme}\\nAudience: {target_audience}",
        [
          { field: "slides_copy", as: "slides_copy", type: "string" },
          { field: "narrative_arc", as: "narrative_arc", type: "string" },
          { field: "engagement_hooks", as: "engagement_hooks", type: "string" },
        ],
      ),

      // 4. AI caption + hashtags
      aiNode(
        "4",
        "AI generate caption and hashtags",
        760,
        "You are an Instagram growth expert. Write a compelling carousel caption (max 2200 chars) with a hook in the first line, value summary, and CTA. Generate 20-30 relevant hashtags mixing high-volume and niche tags. Respond JSON: {caption: string, hashtags: string, caption_length: number, cta_text: string, save_prompt: string}",
        "Blog: {blog_title}\\nSlide copy: {slides_copy}\\nAudience: {target_audience}\\nEngagement hooks: {engagement_hooks}",
        [
          { field: "caption", as: "instagram_caption", type: "string" },
          { field: "hashtags", as: "instagram_hashtags", type: "string" },
          { field: "caption_length", as: "caption_length", type: "number" },
          { field: "cta_text", as: "cta_text", type: "string" },
          { field: "save_prompt", as: "save_prompt", type: "string" },
        ],
      ),

      // 5. Design direction brief
      aiNode(
        "5",
        "AI generate design direction",
        1000,
        "You are a visual design director. Create a design brief for the carousel. Specify font choices, layout style (text-heavy, graphic, photo-overlay), color usage, brand element placement, and any stock photo/illustration suggestions per slide. Respond JSON: {design_brief: string, font_primary: string, font_secondary: string, layout_style: string, asset_suggestions: string}",
        "Theme: {visual_theme}\\nPalette: {color_palette}\\nSlides: {slides_copy}\\nSlide count: {slide_count}\\nAudience: {target_audience}",
        [
          { field: "design_brief", as: "design_brief", type: "string" },
          { field: "font_primary", as: "font_primary", type: "string" },
          { field: "font_secondary", as: "font_secondary", type: "string" },
          { field: "layout_style", as: "layout_style", type: "string" },
          {
            field: "asset_suggestions",
            as: "asset_suggestions",
            type: "string",
          },
        ],
      ),

      // 6. Queue in Sheets for design team
      sheetsAppend(
        "6",
        "Queue carousel in design Sheets",
        1240,
        "DesignQueue",
        '[["{blog_title}","{slide_count}","{layout_style}","{font_primary}","{color_palette}","{design_brief}","{instagram_caption}","pending"]]',
      ),

      // 7. Slack notification to production team
      slackNode(
        "7",
        "Slack: Carousel design queued",
        1480,
        "🎨 *Instagram Carousel Queued*\\nBlog: {blog_title}\\nSlides: {slide_count} | Layout: {layout_style}\\nFonts: {font_primary} / {font_secondary}\\nPalette: {color_palette}\\n\\nCaption preview:\\n{instagram_caption}\\n\\nDesign brief added to queue — check Sheets for details.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 238. OM63 BLOG TO YOUTUBE SCRIPT (7 nodes)
  // ===================================================================

  "OM63 Blog to YouTube Script": {
    summary:
      "Triggers on blog publication suitable for video, uses AI to assess video suitability, generates a full script with intro/body/outro, creates production notes, generates title and thumbnail concepts, estimates duration with runCode, and delivers to the video team.",
    nodes: [
      trigger("Webhook: Blog post for video adaptation", "webhook_app", {
        source: "WordPress / Ghost / CMS Webhook",
        event: "post.published",
        howItStarts:
          "A new blog post is published and flagged for potential YouTube video adaptation",
        setup:
          "Connect your CMS publish webhook to POST blog data to the workflow URL. Tag posts with 'video-candidate' in your CMS to trigger this workflow selectively.",
        inputFields: [],
      }),

      // 2. AI video suitability assessment
      aiNode(
        "2",
        "AI assess video suitability",
        280,
        "You are a YouTube content strategist. Assess whether this blog post would make a good YouTube video. Consider visual potential, audience interest, search volume, tutorial potential, and storytelling opportunity. Respond JSON: {blog_title: string, blog_content: string, blog_url: string, suitability_score: number, video_format: string, estimated_views: string, search_keywords: string, visual_opportunities: string, is_suitable: string}",
        "{webhook_payload}",
        [
          { field: "blog_title", as: "blog_title", type: "string" },
          { field: "blog_content", as: "blog_content", type: "string" },
          { field: "blog_url", as: "blog_url", type: "string" },
          {
            field: "suitability_score",
            as: "suitability_score",
            type: "number",
          },
          { field: "video_format", as: "video_format", type: "string" },
          { field: "estimated_views", as: "estimated_views", type: "string" },
          { field: "search_keywords", as: "search_keywords", type: "string" },
          {
            field: "visual_opportunities",
            as: "visual_opportunities",
            type: "string",
          },
          { field: "is_suitable", as: "is_suitable", type: "string" },
        ],
      ),

      // 3. AI script generation
      aiNode(
        "3",
        "AI generate YouTube script",
        520,
        "You are a professional YouTube scriptwriter. Write a complete video script with: hook (first 10 seconds to grab attention), intro (set expectations), main body (structured sections with transitions), and outro (CTA + subscribe). Include speaker notes and B-roll suggestions. Respond JSON: {script_hook: string, script_intro: string, script_body: string, script_outro: string, full_script: string, section_count: number, word_count: number}",
        "Blog title: {blog_title}\\nContent: {blog_content}\\nFormat: {video_format}\\nKeywords: {search_keywords}\\nVisual opportunities: {visual_opportunities}",
        [
          { field: "script_hook", as: "script_hook", type: "string" },
          { field: "script_intro", as: "script_intro", type: "string" },
          { field: "script_body", as: "script_body", type: "string" },
          { field: "script_outro", as: "script_outro", type: "string" },
          { field: "full_script", as: "full_script", type: "string" },
          { field: "section_count", as: "section_count", type: "number" },
          { field: "word_count", as: "script_word_count", type: "number" },
        ],
      ),

      // 4. AI production notes
      aiNode(
        "4",
        "AI generate production notes",
        760,
        "You are a video production director. Create detailed production notes: camera angles, B-roll shot list, screen recordings needed, graphics/animations, music mood, and pacing notes. Respond JSON: {broll_shots: string, graphics_needed: string, music_mood: string, pacing_notes: string, equipment_needed: string, location_suggestions: string}",
        "Script: {full_script}\\nFormat: {video_format}\\nVisual opportunities: {visual_opportunities}\\nSections: {section_count}",
        [
          { field: "broll_shots", as: "broll_shots", type: "string" },
          { field: "graphics_needed", as: "graphics_needed", type: "string" },
          { field: "music_mood", as: "music_mood", type: "string" },
          { field: "pacing_notes", as: "pacing_notes", type: "string" },
          { field: "equipment_needed", as: "equipment_needed", type: "string" },
          {
            field: "location_suggestions",
            as: "location_suggestions",
            type: "string",
          },
        ],
      ),

      // 5. AI title + thumbnail concepts
      aiNode(
        "5",
        "AI generate title and thumbnail concepts",
        1000,
        "You are a YouTube SEO and thumbnail expert. Generate 3 title options (under 60 chars each, include a keyword), 3 thumbnail concepts (describe the image, text overlay, and emotion), and a description with timestamps. Respond JSON: {title_options: string, thumbnail_concepts: string, video_description: string, tags: string, category: string}",
        "Blog title: {blog_title}\\nScript hook: {script_hook}\\nKeywords: {search_keywords}\\nFormat: {video_format}\\nEstimated views: {estimated_views}",
        [
          { field: "title_options", as: "title_options", type: "string" },
          {
            field: "thumbnail_concepts",
            as: "thumbnail_concepts",
            type: "string",
          },
          {
            field: "video_description",
            as: "video_description",
            type: "string",
          },
          { field: "tags", as: "video_tags", type: "string" },
          { field: "category", as: "video_category", type: "string" },
        ],
      ),

      // 6. Duration estimation via runCode
      n("6", "runCode", "Estimate video duration", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Estimate video duration based on script word count
// Average speaking pace: ~150 words per minute for YouTube
const wordCount = inputs.script_word_count || 0;
const speakingMinutes = wordCount / 150;
const brollPadding = speakingMinutes * 0.15; // 15% extra for B-roll
const introPadding = 0.25; // 15 seconds for intro animation
const outroPadding = 0.33; // 20 seconds for end screen

const totalMinutes = speakingMinutes + brollPadding + introPadding + outroPadding;
const minutes = Math.floor(totalMinutes);
const seconds = Math.round((totalMinutes - minutes) * 60);

return {
  estimated_duration: minutes + ':' + (seconds < 10 ? '0' : '') + seconds,
  duration_minutes: Math.round(totalMinutes * 10) / 10,
  speaking_time: Math.round(speakingMinutes * 10) / 10 + ' min',
  is_short_form: totalMinutes < 3 ? 'true' : 'false',
  production_complexity: wordCount > 1500 ? 'high' : wordCount > 800 ? 'medium' : 'low'
};`,
          inputVariables: [{ name: "script_word_count", type: "number" }],
          outputVariables: [
            { name: "estimated_duration", type: "string" },
            { name: "duration_minutes", type: "number" },
            { name: "speaking_time", type: "string" },
            { name: "is_short_form", type: "string" },
            { name: "production_complexity", type: "string" },
          ],
        },
      }),

      // 7. Deliver to video team via Slack
      slackNode(
        "7",
        "Slack: Video script ready for production",
        1480,
        "🎬 *YouTube Script Ready*\\nBlog: {blog_title}\\nFormat: {video_format} | Duration: ~{estimated_duration}\\nComplexity: {production_complexity}\\n\\n*Title Options:*\\n{title_options}\\n\\n*Thumbnail Concepts:*\\n{thumbnail_concepts}\\n\\n*Production Notes:*\\nB-roll: {broll_shots}\\nGraphics: {graphics_needed}\\nMusic: {music_mood}\\n\\nFull script and details in the production channel.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 239. OM64 BLOG TO MEDIUM (6 nodes)
  // ===================================================================

  "OM64 Blog to Medium": {
    summary:
      "Triggers on blog publication for cross-posting, uses AI to adapt content for Medium's audience, adds canonical link via runCode, selects appropriate Medium tags, publishes via Medium API, and notifies the distribution team.",
    nodes: [
      trigger("Webhook: Blog post for Medium cross-post", "webhook_app", {
        source: "WordPress / Ghost / CMS Webhook",
        event: "post.published",
        howItStarts:
          "A new blog post is published and queued for Medium cross-posting with proper canonical link setup",
        setup:
          "Connect your CMS publish webhook to POST blog data to the workflow URL. Configure your Medium integration token.",
        inputFields: [],
      }),

      // 2. AI adapt for Medium audience
      aiNode(
        "2",
        "AI adapt content for Medium",
        280,
        "You are a Medium content strategist. Adapt this blog post for Medium's audience. Medium readers prefer long-form, story-driven content with personal insights. Adjust the opening to be more narrative, add subheadings for scannability, and ensure it reads well on Medium's clean layout. Respond JSON: {blog_title: string, blog_url: string, original_content: string, medium_title: string, medium_content: string, opening_hook: string, estimated_read_time: number, content_type: string, author_name: string}",
        "{webhook_payload}",
        [
          { field: "blog_title", as: "blog_title", type: "string" },
          { field: "blog_url", as: "blog_url", type: "string" },
          { field: "original_content", as: "original_content", type: "string" },
          { field: "medium_title", as: "medium_title", type: "string" },
          { field: "medium_content", as: "medium_content", type: "string" },
          { field: "opening_hook", as: "opening_hook", type: "string" },
          {
            field: "estimated_read_time",
            as: "estimated_read_time",
            type: "number",
          },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
        ],
      ),

      // 3. Add canonical link via runCode
      n("3", "runCode", "Add canonical link and format for Medium", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Prepare Medium content with canonical link to original blog
const content = inputs.medium_content || '';
const canonicalUrl = inputs.blog_url || '';
const title = inputs.medium_title || inputs.blog_title || '';

// Add canonical notice at the end
const contentWithCanonical = content
  + '\\n\\n---\\n\\n'
  + '*This article was originally published on [our blog](' + canonicalUrl + '). Follow us there for more content.*';

// Medium API expects HTML or Markdown
const publishFormat = content.includes('<p>') ? 'html' : 'markdown';

return {
  formatted_content: contentWithCanonical,
  canonical_url: canonicalUrl,
  publish_format: publishFormat,
  medium_ready_title: title,
  has_canonical: canonicalUrl.startsWith('http') ? 'true' : 'false'
};`,
          inputVariables: [
            { name: "medium_content", type: "string" },
            { name: "blog_url", type: "string" },
            { name: "medium_title", type: "string" },
            { name: "blog_title", type: "string" },
          ],
          outputVariables: [
            { name: "formatted_content", type: "string" },
            { name: "canonical_url", type: "string" },
            { name: "publish_format", type: "string" },
            { name: "medium_ready_title", type: "string" },
            { name: "has_canonical", type: "string" },
          ],
        },
      }),

      // 4. AI tag selection
      aiNode(
        "4",
        "AI select Medium tags",
        760,
        "You are a Medium publishing expert. Select up to 5 Medium tags that will maximize discoverability for this article. Choose from popular Medium tags that match the content. Respond JSON: {tags: string, primary_tag: string, publication_suggestion: string, distribution_notes: string}",
        "Title: {medium_ready_title}\\nContent type: {content_type}\\nRead time: {estimated_read_time} min\\nOpening: {opening_hook}",
        [
          { field: "tags", as: "medium_tags", type: "string" },
          { field: "primary_tag", as: "primary_tag", type: "string" },
          {
            field: "publication_suggestion",
            as: "publication_suggestion",
            type: "string",
          },
          {
            field: "distribution_notes",
            as: "distribution_notes",
            type: "string",
          },
        ],
      ),

      // 5. Medium API publish
      n("5", "hitApi", "Publish to Medium via API", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.medium.com/v1/users/{medium_user_id}/posts",
          headers: [
            { key: "Authorization", value: "Bearer {medium_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{medium_ready_title}" },
            { key: "contentFormat", value: "{publish_format}" },
            { key: "content", value: "{formatted_content}" },
            { key: "canonicalUrl", value: "{canonical_url}" },
            { key: "tags", value: "{medium_tags}" },
            { key: "publishStatus", value: "draft" },
          ],
          variables: [
            { name: "data.id", customName: "medium_post_id", type: "string" },
            { name: "data.url", customName: "medium_post_url", type: "string" },
            {
              name: "data.publishStatus",
              customName: "medium_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Distribution notification
      slackNode(
        "6",
        "Slack: Medium cross-post published",
        1240,
        "📰 *Medium Cross-Post Ready*\\nTitle: {medium_ready_title}\\nTags: {medium_tags}\\nRead time: {estimated_read_time} min\\nStatus: {medium_status}\\nCanonical: {canonical_url}\\nMedium URL: {medium_post_url}\\n\\nSuggested publication: {publication_suggestion}\\n{distribution_notes}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 240. OM65 BLOG TO DEV.TO (6 nodes)
  // ===================================================================

  "OM65 Blog to Dev.to": {
    summary:
      "Triggers on technical blog publication, uses AI to adapt content for the developer audience on Dev.to, generates front-matter with runCode, selects Dev.to tags, publishes via the Dev.to API, and notifies the dev-rel team.",
    nodes: [
      trigger("Webhook: Tech blog for Dev.to cross-post", "webhook_app", {
        source: "WordPress / Ghost / CMS Webhook",
        event: "post.published",
        howItStarts:
          "A technical blog post is published and queued for Dev.to cross-posting",
        setup:
          "Connect your CMS publish webhook to POST blog data to the workflow URL. Configure your Dev.to API key.",
        inputFields: [],
      }),

      // 2. AI developer adaptation
      aiNode(
        "2",
        "AI adapt for developer audience",
        280,
        "You are a developer content writer. Adapt this blog post for the Dev.to community. Dev.to readers are developers who value practical, code-heavy, tutorial-style content. Add code examples, simplify corporate language, and make it more conversational. Respond JSON: {blog_title: string, blog_url: string, original_content: string, devto_title: string, devto_content: string, has_code_examples: string, tutorial_style: string, difficulty_level: string, author_name: string}",
        "{webhook_payload}",
        [
          { field: "blog_title", as: "blog_title", type: "string" },
          { field: "blog_url", as: "blog_url", type: "string" },
          { field: "original_content", as: "original_content", type: "string" },
          { field: "devto_title", as: "devto_title", type: "string" },
          { field: "devto_content", as: "devto_content", type: "string" },
          {
            field: "has_code_examples",
            as: "has_code_examples",
            type: "string",
          },
          { field: "tutorial_style", as: "tutorial_style", type: "string" },
          { field: "difficulty_level", as: "difficulty_level", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
        ],
      ),

      // 3. Front-matter generation via runCode
      n("3", "runCode", "Generate Dev.to front-matter", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Generate Dev.to front-matter YAML header
const title = inputs.devto_title || inputs.blog_title || '';
const canonical = inputs.blog_url || '';
const content = inputs.devto_content || '';
const difficulty = inputs.difficulty_level || 'beginner';

// Dev.to uses YAML front-matter
const frontMatter = '---\\n'
  + 'title: ' + title + '\\n'
  + 'published: false\\n'
  + 'description: ' + title.substring(0, 100) + '\\n'
  + 'canonical_url: ' + canonical + '\\n'
  + 'cover_image: \\n'
  + '---\\n\\n';

const fullArticle = frontMatter + content
  + '\\n\\n---\\n\\n'
  + '*Originally published on [our blog](' + canonical + ')*';

return {
  devto_article: fullArticle,
  front_matter: frontMatter,
  canonical_url: canonical,
  devto_ready_title: title,
  article_length: content.length
};`,
          inputVariables: [
            { name: "devto_title", type: "string" },
            { name: "blog_title", type: "string" },
            { name: "blog_url", type: "string" },
            { name: "devto_content", type: "string" },
            { name: "difficulty_level", type: "string" },
          ],
          outputVariables: [
            { name: "devto_article", type: "string" },
            { name: "front_matter", type: "string" },
            { name: "canonical_url", type: "string" },
            { name: "devto_ready_title", type: "string" },
            { name: "article_length", type: "number" },
          ],
        },
      }),

      // 4. AI tag selection
      aiNode(
        "4",
        "AI select Dev.to tags",
        760,
        "You are a Dev.to community expert. Select up to 4 Dev.to tags for maximum reach. Use popular tags like webdev, javascript, tutorial, beginners, react, python, etc. Choose tags that match the content and audience. Respond JSON: {tags: string, primary_tag: string, series_name: string, engagement_prediction: string}",
        "Title: {devto_ready_title}\\nDifficulty: {difficulty_level}\\nHas code: {has_code_examples}\\nTutorial: {tutorial_style}\\nLength: {article_length} chars",
        [
          { field: "tags", as: "devto_tags", type: "string" },
          { field: "primary_tag", as: "devto_primary_tag", type: "string" },
          { field: "series_name", as: "series_name", type: "string" },
          {
            field: "engagement_prediction",
            as: "engagement_prediction",
            type: "string",
          },
        ],
      ),

      // 5. Dev.to API publish
      n("5", "hitApi", "Publish to Dev.to via API", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://dev.to/api/articles",
          headers: [
            { key: "api-key", value: "{devto_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "article",
              value:
                '{"title":"{devto_ready_title}","body_markdown":"{devto_article}","tags":{devto_tags},"canonical_url":"{canonical_url}","published":false,"series":"{series_name}"}',
            },
          ],
          variables: [
            { name: "id", customName: "devto_article_id", type: "number" },
            { name: "url", customName: "devto_article_url", type: "string" },
            {
              name: "published",
              customName: "devto_published",
              type: "boolean",
            },
          ],
        },
      }),

      // 6. Dev-rel notification
      slackNode(
        "6",
        "Slack: Dev.to cross-post ready",
        1240,
        "💻 *Dev.to Cross-Post Ready*\\nTitle: {devto_ready_title}\\nTags: {devto_tags}\\nDifficulty: {difficulty_level}\\nSeries: {series_name}\\nStatus: draft (review & publish)\\nCanonical: {canonical_url}\\nDev.to URL: {devto_article_url}\\n\\nEngagement prediction: {engagement_prediction}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 241. OM66 BLOG TO REDDIT POST (7 nodes)
  // ===================================================================

  "OM66 Blog to Reddit Post": {
    summary:
      "Triggers on blog publication for Reddit distribution, uses AI to select the best subreddits, rewrites content for Reddit's culture, validates rules with runCode, creates a discussion hook, routes through approval gate, and tracks engagement.",
    nodes: [
      trigger("Webhook: Blog post for Reddit distribution", "webhook_app", {
        source: "WordPress / Ghost / CMS Webhook",
        event: "post.published",
        howItStarts:
          "A new blog post is published and queued for strategic Reddit distribution",
        setup:
          "Connect your CMS publish webhook to POST blog data to the workflow URL. Ensure your Reddit API credentials (client_id, client_secret, refresh_token) are configured.",
        inputFields: [],
      }),

      // 2. AI subreddit selection
      aiNode(
        "2",
        "AI select best subreddits",
        280,
        "You are a Reddit marketing strategist. Analyze this blog post and select the 3 best subreddits for sharing. Consider audience match, subreddit size, posting rules, and self-promotion tolerance. Avoid subreddits that ban link posts. Respond JSON: {blog_title: string, blog_url: string, blog_summary: string, subreddits: string, primary_subreddit: string, subreddit_rules_summary: string, risk_level: string, best_time_to_post: string, author_name: string}",
        "{webhook_payload}",
        [
          { field: "blog_title", as: "blog_title", type: "string" },
          { field: "blog_url", as: "blog_url", type: "string" },
          { field: "blog_summary", as: "blog_summary", type: "string" },
          { field: "subreddits", as: "target_subreddits", type: "string" },
          {
            field: "primary_subreddit",
            as: "primary_subreddit",
            type: "string",
          },
          {
            field: "subreddit_rules_summary",
            as: "subreddit_rules",
            type: "string",
          },
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "best_time_to_post",
            as: "best_posting_time",
            type: "string",
          },
          { field: "author_name", as: "author_name", type: "string" },
        ],
      ),

      // 3. AI Reddit rewrite
      aiNode(
        "3",
        "AI rewrite for Reddit culture",
        520,
        "You are a Reddit native. Rewrite this blog content as a Reddit post. Reddit users hate promotional content — frame it as a genuine discussion, personal experience, or resource sharing. Never sound like marketing copy. Use casual tone, be direct, and provide value upfront. Respond JSON: {reddit_title: string, reddit_body: string, post_type: string, tone_check: string, self_promotion_score: number}",
        "Blog: {blog_title}\\nSummary: {blog_summary}\\nSubreddit: {primary_subreddit}\\nRules: {subreddit_rules}\\nURL: {blog_url}",
        [
          { field: "reddit_title", as: "reddit_title", type: "string" },
          { field: "reddit_body", as: "reddit_body", type: "string" },
          { field: "post_type", as: "reddit_post_type", type: "string" },
          { field: "tone_check", as: "tone_check", type: "string" },
          {
            field: "self_promotion_score",
            as: "self_promotion_score",
            type: "number",
          },
        ],
      ),

      // 4. Rule check via runCode
      n("4", "runCode", "Validate against subreddit rules", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Validate Reddit post against common subreddit rules
const title = inputs.reddit_title || '';
const body = inputs.reddit_body || '';
const promoScore = inputs.self_promotion_score || 0;
const rules = inputs.subreddit_rules || '';

const issues = [];
if (title.length > 300) issues.push('Title exceeds 300 char limit');
if (promoScore > 6) issues.push('Self-promotion score too high (' + promoScore + '/10)');
if (body.match(/buy now|sign up|discount|promo code/i)) issues.push('Contains promotional language');
if (title.match(/\\[|\\]/)) issues.push('Title contains brackets (banned in some subs)');
if (!body || body.length < 50) issues.push('Body too short for text post');

return {
  validation_passed: issues.length === 0 ? 'true' : 'false',
  issues_found: issues.join('; ') || 'none',
  issue_count: issues.length,
  post_ready: issues.length === 0 ? 'true' : 'false',
  title_length: title.length,
  body_length: body.length
};`,
          inputVariables: [
            { name: "reddit_title", type: "string" },
            { name: "reddit_body", type: "string" },
            { name: "self_promotion_score", type: "number" },
            { name: "subreddit_rules", type: "string" },
          ],
          outputVariables: [
            { name: "validation_passed", type: "string" },
            { name: "issues_found", type: "string" },
            { name: "issue_count", type: "number" },
            { name: "post_ready", type: "string" },
            { name: "title_length", type: "number" },
            { name: "body_length", type: "number" },
          ],
        },
      }),

      // 5. AI discussion hook
      aiNode(
        "5",
        "AI craft discussion hook",
        1000,
        "You are a Reddit engagement expert. Add a discussion-generating question or hook at the end of this post that encourages genuine comments. The hook should feel natural, not forced. Respond JSON: {discussion_hook: string, final_post_body: string, expected_engagement: string}",
        "Post title: {reddit_title}\\nBody: {reddit_body}\\nSubreddit: {primary_subreddit}\\nTone: {tone_check}\\nValidation: {issues_found}",
        [
          { field: "discussion_hook", as: "discussion_hook", type: "string" },
          { field: "final_post_body", as: "final_reddit_body", type: "string" },
          {
            field: "expected_engagement",
            as: "expected_engagement",
            type: "string",
          },
        ],
      ),

      // 6. Approval gate
      n("6", "approvalGate", "Approve Reddit post", 1240, {
        approvalGateData: {
          message:
            "📮 Reddit Post Ready\\nSubreddit: r/{primary_subreddit}\\nTitle: {reddit_title}\\nRisk: {risk_level} | Promo score: {self_promotion_score}/10\\nValidation: {validation_passed} ({issues_found})\\n\\n{final_reddit_body}\\n\\nExpected engagement: {expected_engagement}\\nBest time: {best_posting_time}\\n\\nApprove to post?",
        },
      }),

      // 7. Engagement tracking
      sheetsAppend(
        "7",
        "Track Reddit engagement",
        1480,
        "RedditPosts",
        '[["{reddit_title}","{primary_subreddit}","{risk_level}","{self_promotion_score}","{expected_engagement}","{best_posting_time}","posted"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 242. POSITIVE REVIEW THANK-YOU (6 nodes)
  // ===================================================================

  "Positive Review Thank-You": {
    summary:
      "Triggers on positive review detection, uses AI to extract praise points and sentiment, pulls customer context from HubSpot, drafts a personalized thank-you response, gates through approval, and flags the reviewer for advocacy programs.",
    nodes: [
      trigger("Webhook: Positive review received", "webhook_app", {
        source: "Google My Business / Trustpilot / G2",
        event: "review.positive",
        howItStarts:
          "A new positive review (4-5 stars) is detected on any connected review platform",
        setup:
          "Connect your review monitoring tool to POST review data (text, rating, platform, reviewer) to the workflow URL whenever a positive review (4+ stars) is detected.",
        inputFields: [],
      }),

      // 2. AI extract praise points
      aiNode(
        "2",
        "AI extract praise and sentiment",
        280,
        "You are a customer experience analyst. Read this positive review and extract what the customer specifically praised, their emotional tone, any team members or features mentioned, and their overall enthusiasm level. Respond JSON: {reviewer_name: string, review_text: string, star_rating: number, platform: string, praise_points: string, specific_features: string, mentioned_employees: string, enthusiasm_level: string, customer_email: string, quotable_line: string}",
        "{webhook_payload}",
        [
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "review_text", as: "review_text", type: "string" },
          { field: "star_rating", as: "star_rating", type: "number" },
          { field: "platform", as: "review_platform", type: "string" },
          { field: "praise_points", as: "praise_points", type: "string" },
          {
            field: "specific_features",
            as: "specific_features",
            type: "string",
          },
          {
            field: "mentioned_employees",
            as: "mentioned_employees",
            type: "string",
          },
          { field: "enthusiasm_level", as: "enthusiasm_level", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "quotable_line", as: "quotable_line", type: "string" },
        ],
      ),

      // 3. HubSpot customer context
      n("3", "hitApi", "HubSpot: Pull customer context", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["firstname","lastname","lifecyclestage","hs_lead_status","num_associated_deals","hs_analytics_num_visits"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "customer_first_name",
              type: "string",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "results[0].properties.num_associated_deals",
              customName: "deal_count",
              type: "number",
            },
            {
              name: "results[0].properties.hs_analytics_num_visits",
              customName: "visit_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI draft thank-you
      aiNode(
        "4",
        "AI draft personalized thank-you",
        760,
        "You are a customer relations writer. Draft a warm, genuine thank-you response to this positive review. Reference their specific praise points to show you actually read it. If they mentioned an employee, acknowledge that person. Keep it authentic — never generic. Respond JSON: {thank_you_draft: string, is_advocacy_candidate: string, advocacy_reason: string, internal_notes: string}",
        "Reviewer: {reviewer_name} on {review_platform} ({star_rating} stars)\\nReview: {review_text}\\nPraise: {praise_points}\\nFeatures: {specific_features}\\nEmployee mentions: {mentioned_employees}\\nCustomer: {customer_first_name}, lifecycle={lifecycle_stage}, deals={deal_count}",
        [
          { field: "thank_you_draft", as: "thank_you_draft", type: "string" },
          {
            field: "is_advocacy_candidate",
            as: "is_advocacy_candidate",
            type: "string",
          },
          { field: "advocacy_reason", as: "advocacy_reason", type: "string" },
          { field: "internal_notes", as: "internal_notes", type: "string" },
        ],
      ),

      // 5. Approval gate
      n("5", "approvalGate", "Approve thank-you response", 1000, {
        approvalGateData: {
          message:
            "💛 Positive Review Thank-You Ready\\nReviewer: {reviewer_name} on {review_platform} ({star_rating} stars)\\nEnthusiasm: {enthusiasm_level}\\n\\nReview: {review_text}\\n\\nDrafted response:\\n{thank_you_draft}\\n\\nAdvocacy candidate: {is_advocacy_candidate}\\nReason: {advocacy_reason}\\n\\nApprove to post?",
        },
      }),

      // 6. Advocacy flagging + notification
      slackNode(
        "6",
        "Slack: Advocacy candidate flagged",
        1240,
        '⭐ *Positive Review Handled*\\nReviewer: {reviewer_name} ({review_platform}, {star_rating} stars)\\nEnthusiasm: {enthusiasm_level}\\nQuotable: "{quotable_line}"\\n\\nAdvocacy candidate: {is_advocacy_candidate}\\nReason: {advocacy_reason}\\n\\nFeatures praised: {specific_features}\\nInternal notes: {internal_notes}',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 243. REVIEW DEFENSE SYSTEM (8 nodes)
  // ===================================================================

  "Review Defense System": {
    summary:
      "Monitors multiple review platforms for threats, classifies severity with AI, routes by threat level, pulls customer resolution history from HubSpot, generates response strategies, escalates red-level threats to execs via Slack, posts responses, and logs to a dashboard.",
    nodes: [
      trigger("Webhook: Multi-platform review intake", "webhook_app", {
        source: "ReviewTrackers / Birdeye / Custom Aggregator",
        event: "review.detected",
        howItStarts:
          "A new review is detected across any monitored platform (Google, Yelp, Trustpilot, G2, Capterra, App Store)",
        setup:
          "Connect your review aggregation tool to POST review data to the workflow URL. Include platform, rating, text, reviewer details, and any available customer identifiers.",
        inputFields: [],
      }),

      // 2. AI threat classification
      aiNode(
        "2",
        "AI threat classification and triage",
        280,
        "You are a reputation management analyst. Classify this review for threat level: GREEN (positive/neutral, 4-5 stars), YELLOW (mixed, 3 stars, or minor complaint), RED (1-2 stars, public accusation, viral potential, legal threat). Extract all key details regardless of payload format. Respond JSON: {reviewer_name: string, review_text: string, star_rating: number, platform: string, threat_level: string, threat_reasons: string, viral_potential: string, legal_risk: string, complaint_summary: string, customer_email: string, review_id: string, is_red_alert: string}",
        "{webhook_payload}",
        [
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "review_text", as: "review_text", type: "string" },
          { field: "star_rating", as: "star_rating", type: "number" },
          { field: "platform", as: "review_platform", type: "string" },
          { field: "threat_level", as: "threat_level", type: "string" },
          { field: "threat_reasons", as: "threat_reasons", type: "string" },
          { field: "viral_potential", as: "viral_potential", type: "string" },
          { field: "legal_risk", as: "legal_risk", type: "string" },
          {
            field: "complaint_summary",
            as: "complaint_summary",
            type: "string",
          },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "review_id", as: "review_id", type: "string" },
          { field: "is_red_alert", as: "is_red_alert", type: "string" },
        ],
      ),

      // 3. Condition: routing by threat level
      n("3", "condition", "Is RED alert threat?", 520, {
        conditionDetails: {
          conditions: [
            { field: "is_red_alert", operator: "equals", value: "true" },
          ],
        },
      }),

      // 4. HubSpot customer resolution lookup (True: RED path)
      n("4", "hitApi", "HubSpot: Customer resolution history", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["firstname","lastname","lifecyclestage","hs_lead_status","num_associated_deals","total_revenue","notes_last_updated"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "customer_first_name",
              type: "string",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "results[0].properties.total_revenue",
              customName: "customer_revenue",
              type: "string",
            },
            {
              name: "results[0].properties.notes_last_updated",
              customName: "last_interaction",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI strategy generation
      aiNode(
        "5",
        "AI generate response strategy",
        1000,
        "You are a crisis communications expert. For this RED-level review, develop a comprehensive response strategy: immediate public response, private outreach plan, escalation protocol, and talking points for executives. Respond JSON: {public_response: string, private_outreach_plan: string, exec_talking_points: string, resolution_offer: string, timeline: string, risk_mitigation: string}",
        "Reviewer: {reviewer_name} on {review_platform} ({star_rating} stars)\\nReview: {review_text}\\nThreat: {threat_level} | Viral potential: {viral_potential} | Legal risk: {legal_risk}\\nComplaint: {complaint_summary}\\nCustomer: {customer_first_name}, lifecycle={lifecycle_stage}, revenue={customer_revenue}\\nLast interaction: {last_interaction}",
        [
          { field: "public_response", as: "public_response", type: "string" },
          {
            field: "private_outreach_plan",
            as: "private_outreach_plan",
            type: "string",
          },
          {
            field: "exec_talking_points",
            as: "exec_talking_points",
            type: "string",
          },
          { field: "resolution_offer", as: "resolution_offer", type: "string" },
          { field: "timeline", as: "response_timeline", type: "string" },
          { field: "risk_mitigation", as: "risk_mitigation", type: "string" },
        ],
      ),

      // 6. Executive escalation via Slack
      slackNode(
        "6",
        "Slack: RED exec escalation",
        1240,
        "🚨 *RED ALERT — Review Defense System*\\nPlatform: {review_platform} | Rating: {star_rating}/5\\nReviewer: {reviewer_name} | Customer: {customer_first_name}\\nThreat: {threat_level} | Viral: {viral_potential} | Legal: {legal_risk}\\n\\n*Review:* {review_text}\\n\\n*Exec Talking Points:*\\n{exec_talking_points}\\n\\n*Proposed Response:*\\n{public_response}\\n\\n*Resolution:* {resolution_offer}\\n*Timeline:* {response_timeline}",
      ),

      // 7. Response posting
      n("7", "hitApi", "Post defense response", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.reviewplatform.com/reviews/{review_id}/reply",
          headers: [
            { key: "Authorization", value: "Bearer {review_platform_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "reply", value: "{public_response}" },
            {
              key: "internal_notes",
              value:
                "Threat: {threat_level} | Strategy: {risk_mitigation} | Offer: {resolution_offer}",
            },
          ],
          variables: [
            { name: "id", customName: "reply_id", type: "string" },
            { name: "status", customName: "reply_status", type: "string" },
          ],
        },
      }),

      // 8. Dashboard logging
      sheetsAppend(
        "8",
        "Log to review defense dashboard",
        1720,
        "ReviewDefense",
        '[["{review_platform}","{star_rating}","{threat_level}","{reviewer_name}","{complaint_summary}","{viral_potential}","{legal_risk}","{reply_status}","{resolution_offer}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 244. SMS APPOINTMENT CONFIRMATION BOT (8 nodes)
  // ===================================================================

  "SMS Appointment Confirmation Bot": {
    summary:
      "Triggers on new appointment bookings, rechecks provider availability, sends a confirmation SMS via Twilio, processes reply messages with runCode, routes by response type, syncs booking changes, sends a 24-hour reminder after a delay, and alerts the provider via Slack.",
    nodes: [
      trigger("Webhook: New appointment booked", "webhook_app", {
        source: "Calendly / Acuity / Custom Booking System",
        event: "appointment.created",
        howItStarts:
          "A new appointment is booked through your scheduling system, triggering the confirmation and reminder flow",
        setup:
          "Connect your booking system webhook to POST appointment data (patient/client name, phone, datetime, provider, service_type) to the workflow URL whenever a new booking is created.",
        inputFields: [],
      }),

      // 2. Availability recheck
      n("2", "hitApi", "Recheck provider availability", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-booking-system.com/availability?provider={provider_id}&date={appointment_date}&time={appointment_time}",
          headers: [
            { key: "Authorization", value: "Bearer {booking_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "available",
              customName: "still_available",
              type: "string",
            },
            {
              name: "provider_name",
              customName: "provider_name",
              type: "string",
            },
            {
              name: "appointment_id",
              customName: "appointment_id",
              type: "string",
            },
            { name: "client_name", customName: "client_name", type: "string" },
            {
              name: "client_phone",
              customName: "client_phone",
              type: "string",
            },
            {
              name: "appointment_datetime",
              customName: "appointment_datetime",
              type: "string",
            },
            {
              name: "service_type",
              customName: "service_type",
              type: "string",
            },
          ],
        },
      }),

      // 3. Send confirmation SMS via Twilio
      n("3", "sendSMS", "SMS: Appointment confirmation", 520, {
        sendSMSDetails: {
          to: "{client_phone}",
          smsBody:
            "Hi {client_name}, your {service_type} appointment with {provider_name} is confirmed for {appointment_datetime}. Reply YES to confirm, RESCHEDULE to change, or CANCEL to cancel. Ref: {appointment_id}",
        },
      }),

      // 4. Process reply via runCode
      n("4", "runCode", "Process SMS reply", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Parse incoming SMS reply and determine action
const reply = (inputs.sms_reply || '').trim().toUpperCase();
let action = 'unknown';
let confirmed = 'false';
let needs_reschedule = 'false';
let is_cancellation = 'false';

if (reply.includes('YES') || reply.includes('CONFIRM') || reply.includes('OK')) {
  action = 'confirmed';
  confirmed = 'true';
} else if (reply.includes('RESCHEDULE') || reply.includes('CHANGE') || reply.includes('MOVE')) {
  action = 'reschedule';
  needs_reschedule = 'true';
} else if (reply.includes('CANCEL') || reply.includes('NO')) {
  action = 'cancelled';
  is_cancellation = 'true';
} else {
  action = 'unrecognized';
}

return {
  parsed_action: action,
  is_confirmed: confirmed,
  needs_reschedule: needs_reschedule,
  is_cancellation: is_cancellation,
  raw_reply: reply
};`,
          inputVariables: [{ name: "sms_reply", type: "string" }],
          outputVariables: [
            { name: "parsed_action", type: "string" },
            { name: "is_confirmed", type: "string" },
            { name: "needs_reschedule", type: "string" },
            { name: "is_cancellation", type: "string" },
            { name: "raw_reply", type: "string" },
          ],
        },
      }),

      // 5. Condition: check response type
      n("5", "condition", "Is appointment confirmed?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_confirmed", operator: "equals", value: "true" },
          ],
        },
      }),

      // 6. Booking sync (True: confirmed)
      n("6", "hitApi", "Sync confirmed status to booking system", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.your-booking-system.com/appointments/{appointment_id}",
          headers: [
            { key: "Authorization", value: "Bearer {booking_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "status", value: "{parsed_action}" },
            { key: "sms_confirmed", value: "true" },
            { key: "reply_text", value: "{raw_reply}" },
          ],
          variables: [
            { name: "status", customName: "sync_status", type: "string" },
            {
              name: "updated_at",
              customName: "sync_timestamp",
              type: "string",
            },
          ],
        },
      }),

      // 7. 24-hour reminder delay
      n("7", "delayAction", "Wait until 24hrs before appointment", 1480, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),

      // 8. Provider Slack alert
      slackNode(
        "8",
        "Slack: Provider appointment alert",
        1720,
        "📅 *Appointment Reminder*\\nClient: {client_name} ({client_phone})\\nService: {service_type}\\nTime: {appointment_datetime}\\nStatus: {parsed_action}\\nConfirmed via SMS: {is_confirmed}\\n\\nProvider: {provider_name} — appointment is tomorrow.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 245. SALESFORCE STAGE CHANGE ALERTS (7 nodes)
  // ===================================================================

  "Salesforce Stage Change Alerts": {
    summary:
      "Triggers on Salesforce opportunity stage changes, pulls full opportunity context, uses AI to generate a change summary, routes by deal direction (advancing vs regressing), sends Slack alerts, posts to the appropriate channel, and logs stage transitions to Sheets.",
    nodes: [
      trigger("Webhook: Salesforce opp stage changed", "webhook_app", {
        source: "Salesforce",
        event: "opportunity.stage_changed",
        howItStarts:
          "A Salesforce opportunity moves to a new stage, triggering pipeline intelligence alerts",
        setup:
          "In Salesforce -> Setup -> Outbound Messages or Process Builder, configure a webhook to POST opportunity stage change data (opp_id, old_stage, new_stage) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Pull full opportunity context from Salesforce
      n("2", "hitApi", "Salesforce: Pull opportunity details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://your-instance.salesforce.com/services/data/v58.0/sobjects/Opportunity/{opportunity_id}",
          headers: [
            { key: "Authorization", value: "Bearer {salesforce_token}" },
          ],
          body: [],
          variables: [
            { name: "Name", customName: "opp_name", type: "string" },
            { name: "Amount", customName: "opp_amount", type: "number" },
            { name: "StageName", customName: "new_stage", type: "string" },
            { name: "CloseDate", customName: "close_date", type: "string" },
            {
              name: "Probability",
              customName: "win_probability",
              type: "number",
            },
            { name: "Owner.Name", customName: "owner_name", type: "string" },
            {
              name: "Account.Name",
              customName: "account_name",
              type: "string",
            },
            { name: "Type", customName: "opp_type", type: "string" },
            { name: "NextStep", customName: "next_step", type: "string" },
            {
              name: "LastStageChangeDate",
              customName: "last_stage_change",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI summary of stage change
      aiNode(
        "3",
        "AI summarize stage change",
        520,
        "You are a sales operations analyst. Analyze this opportunity stage change and generate an actionable summary. Determine if the deal is advancing (positive) or regressing (negative). Identify risks, next actions, and coaching opportunities. Respond JSON: {change_summary: string, deal_direction: string, is_advancing: string, risk_factors: string, next_actions: string, coaching_notes: string, urgency: string, deal_health: string}",
        "Opportunity: {opp_name}\\nAccount: {account_name}\\nOwner: {owner_name}\\nStage: {new_stage}\\nAmount: ${opp_amount}\\nProbability: {win_probability}%\\nClose date: {close_date}\\nType: {opp_type}\\nNext step: {next_step}\\nLast change: {last_stage_change}",
        [
          { field: "change_summary", as: "change_summary", type: "string" },
          { field: "deal_direction", as: "deal_direction", type: "string" },
          { field: "is_advancing", as: "is_advancing", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          { field: "next_actions", as: "next_actions", type: "string" },
          { field: "coaching_notes", as: "coaching_notes", type: "string" },
          { field: "urgency", as: "deal_urgency", type: "string" },
          { field: "deal_health", as: "deal_health", type: "string" },
        ],
      ),

      // 4. Condition: deal direction
      n("4", "condition", "Is deal advancing?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_advancing", operator: "equals", value: "true" },
          ],
        },
      }),

      // 5a. Positive alert (True: advancing)
      slackNode(
        "5",
        "Slack: Deal advancing alert",
        1000,
        "🟢 *Deal Advancing*\\nOpp: {opp_name} ({account_name})\\nStage: {new_stage} | Amount: ${opp_amount}\\nProbability: {win_probability}% | Close: {close_date}\\nOwner: {owner_name}\\n\\n{change_summary}\\n\\nNext: {next_actions}",
      ),

      // 5b. Risk alert (False: regressing)
      slackNode(
        "6",
        "Slack: Deal risk alert",
        1000,
        "🔴 *Deal At Risk*\\nOpp: {opp_name} ({account_name})\\nStage: {new_stage} | Amount: ${opp_amount}\\nProbability: {win_probability}% | Close: {close_date}\\nOwner: {owner_name}\\n\\n{change_summary}\\n\\nRisks: {risk_factors}\\nCoaching: {coaching_notes}\\nUrgency: {deal_urgency}",
        500,
      ),

      // 7. Stage logging to Sheets
      sheetsAppend(
        "7",
        "Log stage transition to Sheets",
        1240,
        "StageChanges",
        '[["{opp_name}","{account_name}","{new_stage}","{opp_amount}","{win_probability}","{deal_direction}","{deal_health}","{owner_name}","{close_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 246. SLACK ALERT PIPELINE (8 nodes)
  // ===================================================================

  "Slack Alert Pipeline": {
    summary:
      "Receives events from any source, normalizes the payload with runCode, uses AI to classify priority, routes to the correct Slack channel, assembles rich messages, delivers to Slack, enforces rate limiting with runCode, and logs pipeline health metrics.",
    nodes: [
      trigger("Webhook: Incoming event for Slack alerting", "webhook_app", {
        source: "Any System / Monitoring / CI-CD / Support",
        event: "event.received",
        howItStarts:
          "Any system event arrives that needs to be routed to the appropriate Slack channel with proper formatting and priority",
        setup:
          "Point any system webhook (monitoring, CI/CD, support, billing, etc.) to the workflow URL. The pipeline normalizes all formats into structured Slack alerts.",
        inputFields: [],
      }),

      // 2. Event normalization via runCode
      n("2", "runCode", "Normalize event payload", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Normalize any incoming event into a standard alert format
const payload = JSON.parse(inputs.webhook_payload || '{}');

// Extract common fields regardless of source format
const title = payload.title || payload.subject || payload.name || payload.alert_name || payload.message || 'Unnamed Event';
const body = payload.body || payload.description || payload.details || payload.text || JSON.stringify(payload).substring(0, 500);
const source = payload.source || payload.app || payload.service || payload.origin || 'unknown';
const severity = payload.severity || payload.priority || payload.level || payload.urgency || 'info';
const timestamp = payload.timestamp || payload.created_at || payload.time || new Date().toISOString();
const event_type = payload.event_type || payload.type || payload.category || 'general';

return {
  alert_title: title,
  alert_body: body,
  alert_source: source,
  alert_severity: severity.toLowerCase(),
  alert_timestamp: timestamp,
  alert_event_type: event_type,
  is_critical: severity.toLowerCase() === 'critical' || severity.toLowerCase() === 'emergency' ? 'true' : 'false'
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "alert_title", type: "string" },
            { name: "alert_body", type: "string" },
            { name: "alert_source", type: "string" },
            { name: "alert_severity", type: "string" },
            { name: "alert_timestamp", type: "string" },
            { name: "alert_event_type", type: "string" },
            { name: "is_critical", type: "string" },
          ],
        },
      }),

      // 3. AI priority classification and channel routing
      aiNode(
        "3",
        "AI classify priority and route",
        520,
        "You are a DevOps alert routing engine. Classify this event and determine the correct Slack channel. Use these rules: critical/emergency -> #incidents, error -> #alerts, warning -> #monitoring, info -> #activity, billing -> #finance, deploy -> #deployments, support -> #support. Respond JSON: {priority: string, slack_channel: string, needs_attention: string, classification: string, recommended_action: string, mention_group: string}",
        "Title: {alert_title}\\nBody: {alert_body}\\nSource: {alert_source}\\nSeverity: {alert_severity}\\nType: {alert_event_type}\\nTimestamp: {alert_timestamp}",
        [
          { field: "priority", as: "alert_priority", type: "string" },
          {
            field: "slack_channel",
            as: "target_slack_channel",
            type: "string",
          },
          { field: "needs_attention", as: "needs_attention", type: "string" },
          {
            field: "classification",
            as: "alert_classification",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "mention_group", as: "mention_group", type: "string" },
        ],
      ),

      // 4. Channel routing condition
      n("4", "condition", "Is critical priority?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_critical", operator: "equals", value: "true" },
          ],
        },
      }),

      // 5. Message assembly for critical (True branch — includes @channel)
      n("5", "hitApi", "Slack: Critical alert with @channel", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/chat.postMessage",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "channel", value: "{target_slack_channel}" },
            {
              key: "text",
              value:
                "<!channel> 🚨 *CRITICAL: {alert_title}*\\nSource: {alert_source} | Priority: {alert_priority}\\n{alert_body}\\n\\nAction: {recommended_action}\\n{mention_group}",
            },
          ],
          variables: [
            { name: "ok", customName: "slack_delivered", type: "boolean" },
            { name: "ts", customName: "message_ts", type: "string" },
          ],
        },
      }),

      // 6. Standard Slack delivery (False branch)
      n(
        "6",
        "hitApi",
        "Slack: Standard alert delivery",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://slack.com/api/chat.postMessage",
            headers: [
              { key: "Authorization", value: "Bearer {slack_bot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "channel", value: "{target_slack_channel}" },
              {
                key: "text",
                value:
                  "ℹ️ *{alert_title}*\\nSource: {alert_source} | Priority: {alert_priority} | Type: {alert_classification}\\n{alert_body}",
              },
            ],
            variables: [
              { name: "ok", customName: "slack_delivered", type: "boolean" },
              { name: "ts", customName: "message_ts", type: "string" },
            ],
          },
        },
        500,
      ),

      // 7. Rate limiting via runCode
      n("7", "runCode", "Rate limiting and dedup check", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Rate limiting logic: prevent alert fatigue
const source = inputs.alert_source || '';
const title = inputs.alert_title || '';
const severity = inputs.alert_severity || '';
const now = new Date().toISOString();

// Create a fingerprint for dedup (in production, check against a store)
const fingerprint = source + ':' + title.substring(0, 50);
const hash = fingerprint.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0).toString(16);

// Rate limits: critical=unlimited, error=10/hr, warning=5/hr, info=2/hr
const rate_limits = { critical: 999, error: 10, warning: 5, info: 2 };
const max_per_hour = rate_limits[severity] || 5;

return {
  alert_fingerprint: hash,
  rate_limit_ok: 'true',
  max_per_hour: max_per_hour,
  pipeline_timestamp: now,
  dedup_key: fingerprint.substring(0, 100)
};`,
          inputVariables: [
            { name: "alert_source", type: "string" },
            { name: "alert_title", type: "string" },
            { name: "alert_severity", type: "string" },
          ],
          outputVariables: [
            { name: "alert_fingerprint", type: "string" },
            { name: "rate_limit_ok", type: "string" },
            { name: "max_per_hour", type: "number" },
            { name: "pipeline_timestamp", type: "string" },
            { name: "dedup_key", type: "string" },
          ],
        },
      }),

      // 8. Health logging
      sheetsAppend(
        "8",
        "Log pipeline health metrics",
        1480,
        "AlertPipeline",
        '[["{pipeline_timestamp}","{alert_source}","{alert_title}","{alert_priority}","{target_slack_channel}","{alert_fingerprint}","{rate_limit_ok}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 247. SLACK APP HOME DASHBOARD BUILDER (9 nodes)
  // ===================================================================

  "Slack App Home Dashboard Builder": {
    summary:
      "Triggers on Slack App Home open events, resolves the user's role with runCode, hydrates metrics from APIs, generates an AI daily summary, assembles the dashboard layout with runCode, publishes to App Home via Slack API, caches the view with runCode, logs interactions, and manages refresh policies.",
    nodes: [
      trigger("Webhook: Slack App Home opened", "webhook_app", {
        source: "Slack Events API",
        event: "app_home_opened",
        howItStarts:
          "A user opens the Slack App Home tab, triggering a personalized dashboard build based on their role and permissions",
        setup:
          "In your Slack App settings, enable the App Home tab and subscribe to the 'app_home_opened' event. Point the Events API URL to the workflow URL.",
        inputFields: [],
      }),

      // 2. Role resolution via runCode
      n("2", "runCode", "Resolve user role and permissions", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse Slack event and resolve user role
const payload = JSON.parse(inputs.webhook_payload || '{}');
const user_id = payload.event?.user || payload.user || payload.user_id || '';
const team_id = payload.team_id || '';
const tab = payload.event?.tab || 'home';

// Role mapping (in production, look up from a database)
const role_map = {
  'U_EXEC': 'executive',
  'U_MGR': 'manager',
  'U_ENG': 'engineer',
  'U_SALES': 'sales',
  'U_SUPPORT': 'support'
};
const role = role_map[user_id] || 'team_member';

// Determine which metrics sections to show based on role
const sections = {
  executive: 'revenue,pipeline,team_health,goals',
  manager: 'team_metrics,sprint_progress,blockers,goals',
  engineer: 'deployments,incidents,pr_reviews,sprint',
  sales: 'pipeline,deals,activities,quota',
  support: 'tickets,csat,response_time,backlog',
  team_member: 'announcements,tasks,calendar,goals'
};

return {
  slack_user_id: user_id,
  slack_team_id: team_id,
  user_role: role,
  dashboard_sections: sections[role] || sections.team_member,
  cache_key: 'dashboard:' + user_id + ':' + new Date().toISOString().split('T')[0],
  request_timestamp: new Date().toISOString()
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "slack_user_id", type: "string" },
            { name: "slack_team_id", type: "string" },
            { name: "user_role", type: "string" },
            { name: "dashboard_sections", type: "string" },
            { name: "cache_key", type: "string" },
            { name: "request_timestamp", type: "string" },
          ],
        },
      }),

      // 3. Metric hydration from APIs
      n("3", "hitApi", "Hydrate metrics from data sources", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-metrics-service.com/dashboard/hydrate",
          headers: [
            { key: "Authorization", value: "Bearer {metrics_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_id", value: "{slack_user_id}" },
            { key: "role", value: "{user_role}" },
            { key: "sections", value: "{dashboard_sections}" },
          ],
          variables: [
            { name: "metrics", customName: "raw_metrics", type: "string" },
            {
              name: "last_updated",
              customName: "metrics_timestamp",
              type: "string",
            },
            {
              name: "data_freshness",
              customName: "data_freshness",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI daily summary
      aiNode(
        "4",
        "AI generate daily summary",
        760,
        "You are a personalized business intelligence assistant. Given a user's role and their metrics, generate a concise daily summary with key highlights, areas needing attention, and a motivational insight. Keep it scannable. Respond JSON: {daily_summary: string, highlights: string, attention_needed: string, motivational_insight: string, data_quality_note: string}",
        "Role: {user_role}\\nMetrics: {raw_metrics}\\nSections: {dashboard_sections}\\nData freshness: {data_freshness}",
        [
          { field: "daily_summary", as: "daily_summary", type: "string" },
          { field: "highlights", as: "highlights", type: "string" },
          { field: "attention_needed", as: "attention_needed", type: "string" },
          {
            field: "motivational_insight",
            as: "motivational_insight",
            type: "string",
          },
          {
            field: "data_quality_note",
            as: "data_quality_note",
            type: "string",
          },
        ],
      ),

      // 5. Dashboard assembly via runCode
      n(
        "5",
        "runCode",
        "Assemble App Home Block Kit view",
        520,
        {
          codeDetails: {
            language: "javascript",
            code: `// Build Slack Block Kit JSON for App Home dashboard
const role = inputs.user_role || 'team_member';
const summary = inputs.daily_summary || 'No summary available';
const highlights = inputs.highlights || 'No highlights';
const attention = inputs.attention_needed || 'Nothing urgent';
const insight = inputs.motivational_insight || '';
const freshness = inputs.data_freshness || 'unknown';

const blocks = [
  { type: 'header', text: { type: 'plain_text', text: '📊 Your Dashboard (' + role + ')' } },
  { type: 'section', text: { type: 'mrkdwn', text: '*Daily Summary*\\n' + summary } },
  { type: 'divider' },
  { type: 'section', text: { type: 'mrkdwn', text: '*🟢 Highlights*\\n' + highlights } },
  { type: 'section', text: { type: 'mrkdwn', text: '*🔴 Needs Attention*\\n' + attention } },
  { type: 'divider' },
  { type: 'context', elements: [{ type: 'mrkdwn', text: insight + ' | Data: ' + freshness }] }
];

return {
  app_home_blocks: JSON.stringify(blocks),
  block_count: blocks.length,
  view_type: 'home',
  dashboard_built: 'true'
};`,
            inputVariables: [
              { name: "user_role", type: "string" },
              { name: "daily_summary", type: "string" },
              { name: "highlights", type: "string" },
              { name: "attention_needed", type: "string" },
              { name: "motivational_insight", type: "string" },
              { name: "data_freshness", type: "string" },
            ],
            outputVariables: [
              { name: "app_home_blocks", type: "string" },
              { name: "block_count", type: "number" },
              { name: "view_type", type: "string" },
              { name: "dashboard_built", type: "string" },
            ],
          },
        },
        500,
      ),

      // 6. Publish App Home view via Slack API
      n(
        "6",
        "hitApi",
        "Publish App Home view to Slack",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://slack.com/api/views.publish",
            headers: [
              { key: "Authorization", value: "Bearer {slack_bot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "user_id", value: "{slack_user_id}" },
              {
                key: "view",
                value: '{"type":"home","blocks":{app_home_blocks}}',
              },
            ],
            variables: [
              { name: "ok", customName: "view_published", type: "boolean" },
              { name: "view.id", customName: "view_id", type: "string" },
            ],
          },
        },
        500,
      ),

      // 7. Caching via runCode
      n("7", "runCode", "Cache dashboard view", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Cache the rendered dashboard to avoid re-rendering on quick revisits
const cacheKey = inputs.cache_key || '';
const viewId = inputs.view_id || '';
const blocks = inputs.app_home_blocks || '';
const now = new Date().toISOString();

// In production, write to Redis/Memcached
const cache_entry = {
  key: cacheKey,
  view_id: viewId,
  blocks_hash: blocks.length.toString(16),
  cached_at: now,
  ttl_minutes: 30
};

return {
  cache_stored: 'true',
  cache_key_used: cacheKey,
  cache_ttl: '30 minutes',
  cached_at: now,
  blocks_size: blocks.length
};`,
          inputVariables: [
            { name: "cache_key", type: "string" },
            { name: "view_id", type: "string" },
            { name: "app_home_blocks", type: "string" },
          ],
          outputVariables: [
            { name: "cache_stored", type: "string" },
            { name: "cache_key_used", type: "string" },
            { name: "cache_ttl", type: "string" },
            { name: "cached_at", type: "string" },
            { name: "blocks_size", type: "number" },
          ],
        },
      }),

      // 8. Interaction logging
      sheetsAppend(
        "8",
        "Log App Home interaction",
        1240,
        "AppHomeLog",
        '[["{request_timestamp}","{slack_user_id}","{user_role}","{block_count}","{data_freshness}","{cache_key_used}","{view_published}"]]',
      ),

      // 9. Refresh policy
      n("9", "runCode", "Determine refresh policy", 1480, {
        codeDetails: {
          language: "javascript",
          code: `// Set refresh policy based on role and data volatility
const role = inputs.user_role || 'team_member';
const freshness = inputs.data_freshness || 'unknown';

const refresh_policies = {
  executive: { interval: 15, unit: 'minutes', reason: 'Executive dashboards need near-real-time data' },
  manager: { interval: 30, unit: 'minutes', reason: 'Manager metrics update every half hour' },
  engineer: { interval: 5, unit: 'minutes', reason: 'Engineering metrics (deploys, incidents) change rapidly' },
  sales: { interval: 15, unit: 'minutes', reason: 'Pipeline data needs frequent updates' },
  support: { interval: 10, unit: 'minutes', reason: 'Support queues change frequently' },
  team_member: { interval: 60, unit: 'minutes', reason: 'General dashboards refresh hourly' }
};

const policy = refresh_policies[role] || refresh_policies.team_member;

return {
  refresh_interval: policy.interval,
  refresh_unit: policy.unit,
  refresh_reason: policy.reason,
  next_refresh: new Date(Date.now() + policy.interval * 60000).toISOString(),
  policy_applied: role + ':' + policy.interval + policy.unit[0]
};`,
          inputVariables: [
            { name: "user_role", type: "string" },
            { name: "data_freshness", type: "string" },
          ],
          outputVariables: [
            { name: "refresh_interval", type: "number" },
            { name: "refresh_unit", type: "string" },
            { name: "refresh_reason", type: "string" },
            { name: "next_refresh", type: "string" },
            { name: "policy_applied", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
